import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaChevronDown, FaChevronRight, FaLink, FaTasks, FaPlus, FaExclamationTriangle, FaTimes, FaPuzzlePiece, FaCheck } from 'react-icons/fa';
import KanbanBoard from '../Client/components/KanbanBoard';
import TaskDetailsModal from '../Client/components/TaskDetailsModal';
import ProjectLinksModal from '../Client/components/ProjectLinksModal';
import AllTasksModal from '../Client/components/AllTasksModal';
import GanttChart from '../Client/components/GanttChart';
import CreateTaskModal from '../Client/components/CreateTaskModal';
import RisksTable from '../Client/components/RisksTable';
import IssuesTable from '../Client/components/IssuesTable';
import useProjectWebSocket from '../../hooks/useProjectWebSocket';
import ClosureCommentModal from '../../components/ClosureCommentModal';
import AttachReportModal from './components/AttachReportModal';
import ShiftSummaryModal from './components/ShiftSummaryModal';
import { apiFetch } from '../../utils/apiAdmin';
import { useOperationQueue } from './hooks/useOperationQueue';
import { applyPendingOperations } from './utils/applyPendingOperations';
import useAdminTaskOperations from './hooks/useAdminTaskOperations';
import useAdminComments from './hooks/useAdminComments';
import useClosureComment from '../../hooks/useClosureComment';
import { buildBlockedTasksMap } from '../../utils/blockedTasks';
import { clampTaskDateUpdates } from '../../utils/fsClamp';
import { confirmProjectWindow } from '../../utils/projectWindow';
import { confirmClampAdjustments, alertImpossibleRange, alertEndBeforeStart } from '../../utils/clampFeedback';
import Tooltip from '../../components/Tooltip';
import EffortInput from '../../components/EffortInput';
import { formatEffort, sumEffort } from '../../utils/effort';
import { coreMainTasks, effortLabel, isOverrun, varianceLabel, isClientLaneTask } from '../../utils/effortDisplay';

// Extract links from custom_attributes_values (Link 1, Link Description 1, etc.)
const extractLinks = (customAttrs) => {
    if (!customAttrs) return [];
    const links = [];
    for (let i = 1; i <= 5; i++) {
        const url = customAttrs[`Link ${i}`];
        const desc = customAttrs[`Link Description ${i}`];
        if (url) {
            links.push({ url, description: desc || url });
        }
    }
    return links;
};

// Get gradient for progress bar (matches Gantt style)
const getProgressGradient = (baseColour) => {
    const lighterVersions = {
        '#64748b': '#94a3b8', // closed - slate
        '#5b9bd5': '#8bbef0', // in progress - blue
        '#9775c4': '#b99de0', // review - purple
        '#5eada4': '#7bc4bc', // ready - muted teal
        '#a1a1aa': '#b0b0b8', // new - grey
        '#dc7c7c': '#e8a0a0', // needs info - red
        '#7c6b9e': '#a090c0', // status row - purple
        '#10b981': '#22c58a', // main task - green
    };
    const lighter = lighterVersions[baseColour] || baseColour;
    return `linear-gradient(to right, ${lighter} 0%, ${baseColour} 100%)`;
};

// Get subtask progress percentage and colour based on sprint workflow position
const getSubtaskProgress = (statusName, isClosed) => {
    if (isClosed) return { percent: 100, colour: '#64748b' }; // Closed = 100%
    const statusLower = (statusName || '').toLowerCase();
    if (statusLower.includes('ready') || statusLower.includes('done')) return { percent: 80, colour: '#5eada4' };
    if (statusLower.includes('review') || statusLower.includes('test')) return { percent: 60, colour: '#9775c4' };
    if (statusLower.includes('progress') || statusLower.includes('doing')) return { percent: 40, colour: '#5b9bd5' };
    if (statusLower.includes('needs info')) return { percent: 20, colour: '#dc7c7c', needsInfo: true };
    return { percent: 0, colour: '#a1a1aa' }; // New = 0% (not started)
};

// Helper to format date as UK format (DD/MM/YYYY)
const formatDateUK = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

// Helper to check if date is overdue
const isOverdue = (dateString) => {
    if (!dateString) return false;
    const dueDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < today;
};

// Helper to check if date is within 2 days
const isWithin2Days = (dateString) => {
    if (!dateString) return false;
    const dueDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    const diffDays = (dueDate - today) / (1000 * 60 * 60 * 24);
    return diffDays >= 0 && diffDays <= 2;
};

// Helper to get due date colour class
const getDueDateColour = (dateString) => {
    if (!dateString) return '';
    if (isOverdue(dateString)) return 'text-red-600';
    if (isWithin2Days(dateString)) return 'text-orange-500';
    return 'text-green-600';
};

// Helper to get due date background colour class
// Red: overdue, Orange: within 2 days, Grey: more than 2 days away
const getDueDateBgColour = (dateString) => {
    if (!dateString) return '';
    if (isOverdue(dateString)) return 'bg-red-100 text-red-700';
    if (isWithin2Days(dateString)) return 'bg-orange-100 text-orange-700';
    return 'bg-gray-100 text-gray-700'; // Far in future - not urgent
};

// Build a map of blocked tasks from issues and dependencies
// Helper to get start date background colour class
// Grey: more than 3 days away, Orange: within 3 days, Green: started (today or past)
const getStartDateBgColour = (dateString) => {
    if (!dateString) return 'bg-gray-100 text-gray-500';
    const startDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    startDate.setHours(0, 0, 0, 0);
    const diffDays = (startDate - today) / (1000 * 60 * 60 * 24);

    if (diffDays <= 0) return 'bg-green-100 text-green-700'; // Started (today or past)
    if (diffDays <= 3) return 'bg-orange-100 text-orange-700'; // Within 3 days
    return 'bg-gray-100 text-gray-500'; // More than 3 days away
};

// Calculate effective date range from subtasks
const getEffectiveDateRange = (task, allTasks) => {
    const subtasks = allTasks.filter(t => t.mainTaskId === task.taskId);
    if (subtasks.length === 0) {
        // No subtasks - use task's own dates
        return { start: task.startDate, end: task.dueDate };
    }

    // Find earliest start date from subtasks
    const startDates = subtasks
        .map(st => st.startDate)
        .filter(Boolean)
        .map(d => new Date(d));
    const start = startDates.length > 0
        ? new Date(Math.min(...startDates)).toISOString().split('T')[0]
        : null;

    // Find latest due date from subtasks
    const endDates = subtasks
        .map(st => st.dueDate)
        .filter(Boolean)
        .map(d => new Date(d));
    const end = endDates.length > 0
        ? new Date(Math.max(...endDates)).toISOString().split('T')[0]
        : null;

    return { start, end };
};

// Get progress colour class
const getProgressColourClass = (progress) => {
    if (progress >= 75) return 'bg-green-500';
    if (progress >= 50) return 'bg-yellow-500';
    if (progress >= 25) return 'bg-orange-500';
    return 'bg-red-500';
};

// Calculate working days between two dates (excludes weekends)
const calculateWorkingDays = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (startDate > endDate) return 0;

    let workingDays = 0;
    const current = new Date(startDate);

    while (current <= endDate) {
        const dayOfWeek = current.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            workingDays++;
        }
        current.setDate(current.getDate() + 1);
    }

    return workingDays;
};

// Assignee Dropdown Component for quick assignment
const AssigneeDropdown = ({ task, members, onAssign, isSubtask }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const dropdownRef = useRef(null);

    console.log('AssigneeDropdown members:', task.taskId, members?.length, members);

    // Filter out "Admin" user
    const filteredMembers = (members || []).filter(m =>
        m.name?.toLowerCase() !== 'admin' &&
        m.username?.toLowerCase() !== 'admin'
    );

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const handleSelect = async (userId) => {
        setIsOpen(false);
        setSaving(true);
        try {
            await onAssign(task.taskId, userId, isSubtask);
        } finally {
            setSaving(false);
        }
    };

    const currentAssignee = task.assignedToName || null;

    // If task is closed, show strikethrough text (no dropdown)
    const isClosed = task.isClosed === true;
    if (isClosed) {
        return currentAssignee ? (
            <span className="text-sm text-gray-400 line-through">{currentAssignee}</span>
        ) : (
            <span className="text-sm text-gray-400 line-through">Unassigned</span>
        );
    }

    if (saving) {
        return (
            <div className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4 text-primary-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-sm text-gray-500">Saving...</span>
            </div>
        );
    }

    return (
        <div ref={dropdownRef} className="relative">
            <button
                onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
                className="text-sm hover:text-primary-600 hover:underline cursor-pointer text-left"
            >
                {currentAssignee ? (
                    <span className="text-gray-600">{currentAssignee}</span>
                ) : (
                    <span className="text-gray-400">Unassigned</span>
                )}
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[180px]">
                    <div className="px-3 py-1.5 text-xs font-medium text-gray-500 border-b border-gray-100">
                        Assign To
                    </div>
                    <button
                        onClick={() => handleSelect(null)}
                        className="w-full px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-100"
                    >
                        Unassigned
                    </button>
                    {filteredMembers.map(member => {
                        const isCurrentAssignee = task.assignedTo === member.email;
                        return (
                        <button
                            key={member.email}
                            onClick={() => handleSelect(member.email)}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 text-gray-700 flex items-center gap-2`}
                            style={isCurrentAssignee ? { background: 'linear-gradient(to bottom, #fef9c3 0%, #fefce8 100%)' } : undefined}
                        >
                            {!isCurrentAssignee && <span className="text-gray-400">+</span>}
                            <span>{member.name || member.email}</span>
                        </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// Status dropdown for quick status change on subtasks
const StatusDropdown = ({ task, statuses, onStatusChange, onClosureRequired, isBlocked, blockers }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const dropdownRef = useRef(null);

    // Filter out archived statuses and sort by order
    const availableStatuses = (statuses || [])
        .filter(s => !s.is_archived)
        .sort((a, b) => a.order - b.order);

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const handleSelect = async (statusId) => {
        const selectedStatus = availableStatuses.find(s => s.id === statusId);
        // Check if selecting a closed status and task isn't already closed
        if (selectedStatus?.isClosed && !task.isClosed && onClosureRequired) {
            setIsOpen(false);
            onClosureRequired(task, statusId);
            return;
        }

        setIsOpen(false);
        setSaving(true);
        try {
            await onStatusChange(task.taskId, statusId);
        } finally {
            setSaving(false);
        }
    };

    const currentStatus = task.statusName || 'New';
    const currentStatusId = task.status;
    const isClosed = task.isClosed === true;

    // Get colour for status
    const getStatusColour = (statusName, statusIsClosed) => {
        if (statusIsClosed) return '#64748b';
        const statusLower = (statusName || '').toLowerCase();
        if (statusLower.includes('ready') || statusLower.includes('done')) return '#5eada4';
        if (statusLower.includes('review') || statusLower.includes('test')) return '#9775c4';
        if (statusLower.includes('progress') || statusLower.includes('doing')) return '#5b9bd5';
        if (statusLower.includes('needs info')) return '#ef4444';
        return '#6b7280';
    };

    // If blocked, show blocked indicator (not clickable)
    if (isBlocked) {
        const issueBlocker = blockers?.find(b => b.type === 'issue');
        return (
            <span className="text-xs font-medium text-red-600">
                Blocked{issueBlocker && ` (${issueBlocker.id})`}
            </span>
        );
    }

    // If task is closed, show strikethrough text (no dropdown)
    if (isClosed) {
        return (
            <span
                className="text-xs font-medium text-gray-400 line-through"
                style={{ color: getStatusColour(currentStatus, true) }}
            >
                {currentStatus}
            </span>
        );
    }

    if (saving) {
        return (
            <div className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4 text-primary-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-xs text-gray-500">Saving...</span>
            </div>
        );
    }

    return (
        <div ref={dropdownRef} className="relative">
            <button
                onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
                className="text-xs font-medium hover:underline cursor-pointer text-left"
                style={{ color: getStatusColour(currentStatus, false) }}
            >
                {currentStatus}
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[140px]">
                    {availableStatuses.map(status => (
                        <button
                            key={status.id}
                            onClick={() => handleSelect(status.id)}
                            className="w-full px-3 py-2 text-left text-xs hover:bg-gray-100"
                            style={{
                                color: getStatusColour(status.name, status.isClosed),
                                background: currentStatusId === status.id ? 'linear-gradient(to bottom, #fef9c3 0%, #fefce8 100%)' : undefined
                            }}
                        >
                            {status.name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// Resource View Component
const ResourceView = ({ tasks, members, onTaskClick }) => {
    // Group tasks by assignee
    const tasksByAssignee = {};
    members.forEach(m => {
        tasksByAssignee[m.name || m.username] = [];
    });
    tasksByAssignee['Unassigned'] = [];

    tasks.forEach(task => {
        const assignee = task.assignedToName || 'Unassigned';
        if (!tasksByAssignee[assignee]) {
            tasksByAssignee[assignee] = [];
        }
        tasksByAssignee[assignee].push(task);
    });

    // Calculate total working days per resource
    const getResourceWorkingDays = (personTasks) => {
        return personTasks.reduce((total, task) => {
            const start = task.startDate || task.dueDate;
            const end = task.dueDate || task.end;
            return total + calculateWorkingDays(start, end);
        }, 0);
    };

    // Calculate date range
    const allDates = tasks.flatMap(t => [
        t.startDate ? new Date(t.startDate) : null,
        t.dueDate ? new Date(t.dueDate) : null
    ]).filter(Boolean);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    allDates.push(today);

    const minDate = allDates.length > 0 ? new Date(Math.min(...allDates)) : new Date();
    const maxDate = allDates.length > 0 ? new Date(Math.max(...allDates)) : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    const totalDays = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24)) || 30;

    const getTaskPosition = (task) => {
        const start = task.startDate ? new Date(task.startDate) : minDate;
        const end = (task.dueDate || task.end) ? new Date(task.dueDate || task.end) : new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
        const left = ((start - minDate) / (1000 * 60 * 60 * 24) / totalDays) * 100;
        const width = (((end - start) / (1000 * 60 * 60 * 24) + 1) / totalDays) * 100;
        return { left: `${Math.max(0, left)}%`, width: `${Math.max(2, width)}%` };
    };

    return (
        <div className="h-full bg-white rounded-lg shadow overflow-hidden flex flex-col">
            <div className="border-b bg-gray-50 p-3 flex-shrink-0">
                <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">Resource View</span>
                    <span className="text-sm text-gray-500">
                        {formatDateUK(minDate)} - {formatDateUK(maxDate)}
                    </span>
                </div>
            </div>
            <div className="flex-1 overflow-auto">
                {Object.entries(tasksByAssignee)
                    .filter(([, personTasks]) => personTasks.length > 0)
                    .map(([name, personTasks]) => {
                        const workingDays = getResourceWorkingDays(personTasks);
                        return (
                        <div key={name} className="flex border-b hover:bg-gray-50">
                            <div className="w-48 flex-shrink-0 p-3 font-medium bg-gray-50 border-r">
                                <div>{name}</div>
                                <div className="text-xs text-gray-500">
                                    {personTasks.length} task{personTasks.length !== 1 ? 's' : ''} · {workingDays} day{workingDays !== 1 ? 's' : ''}
                                </div>
                            </div>
                            <div className="flex-1 relative" style={{ minHeight: `${Math.max(60, personTasks.length * 36 + 8)}px` }}>
                                {personTasks.map((task, idx) => {
                                    const pos = getTaskPosition(task);
                                    return (
                                        <div
                                            key={task.taskId}
                                            className="absolute h-8 rounded cursor-pointer hover:opacity-80 flex items-center px-2 text-xs text-white overflow-hidden"
                                            style={{
                                                left: pos.left,
                                                width: pos.width,
                                                top: `${idx * 36 + 4}px`,
                                                backgroundColor: '#7393a7'
                                            }}
                                            onClick={() => onTaskClick(task.taskId)}
                                            title={`${task.subject || task.text} (${task.progress}%)`}
                                        >
                                            <div
                                                className={`absolute left-0 top-0 bottom-0 ${getProgressColourClass(task.progress)} opacity-60`}
                                                style={{ width: `${task.progress}%` }}
                                            />
                                            <span className="relative z-10 truncate font-semibold text-gray-900">{task.subject || task.text}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                    })}
            </div>
        </div>
    );
};

// Determine if task is in TME swimlane (not Team swimlane)
const isInTMESwimlane = (task, tasks, teamSwimlaneId) => {
    // For subtasks, check parent task's swimlane
    const parentTask = task.mainTaskId
        ? tasks.find(t => t.taskId === task.mainTaskId)
        : task;
    const result = parentTask?.swimlaneId !== teamSwimlaneId;
    console.log('isInTMESwimlane:', { taskId: task.taskId, parentSwim: parentTask?.swimlaneId, teamSwim: teamSwimlaneId, result });
    return result;
};

const AdminClientView = () => {
    const { leadId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [collapsedColumns, setCollapsedColumns] = useState(() => {
        const saved = localStorage.getItem('adminCollapsedEpics');
        return saved ? new Set(JSON.parse(saved)) : new Set(['Archived']);
    });
    const edgeColumnsInitialisedRef = useRef(localStorage.getItem('adminCollapsedEpics') !== null);
    const [collapsedSwimlanes, setCollapsedSwimlanes] = useState(() => {
        const saved = localStorage.getItem('adminCollapsedSwimlanes');
        return saved ? new Set(JSON.parse(saved)) : new Set();
    });
    const [hoveredMainTaskId, setHoveredMainTaskId] = useState(null);
    // Project effort estimate (T474). ADMIN ONLY - this whole page is the
    // admin client view.
    const [editingProjectEffort, setEditingProjectEffort] = useState(false);
    const [savingProjectEffort, setSavingProjectEffort] = useState(false);

    const [epicsCollapsed, setEpicsCollapsed] = useState(() => {
        const saved = localStorage.getItem('adminEpicsCollapsed');
        return saved !== null ? JSON.parse(saved) : true;
    });
    // Hide completed main tasks in the Epics section (persisted, defaults to showing them)
    const [hideCompletedEpics, setHideCompletedEpics] = useState(() => {
        return localStorage.getItem('epicsHideCompleted') === 'true';
    });
    useEffect(() => {
        localStorage.setItem('epicsHideCompleted', hideCompletedEpics.toString());
    }, [hideCompletedEpics]);

    const [blockedTooltip, setBlockedTooltip] = useState(null);
    const blockedTooltipTimeoutRef = useRef(null);

    // Persist collapsed epics to localStorage
    useEffect(() => {
        localStorage.setItem('adminCollapsedEpics', JSON.stringify([...collapsedColumns]));
    }, [collapsedColumns]);

    // Collapse Edge columns by default on first load (when no saved state exists)
    useEffect(() => {
        const cols = data?.project?.epics || [];
        if (!edgeColumnsInitialisedRef.current && cols.length > 0) {
            const edgeColumns = cols.filter(c => c.name.startsWith('Edge')).map(c => c.name);
            if (edgeColumns.length > 0) {
                setCollapsedColumns(prev => {
                    const newSet = new Set(prev);
                    edgeColumns.forEach(name => newSet.add(name));
                    return newSet;
                });
            }
            edgeColumnsInitialisedRef.current = true;
        }
    }, [data]);

    // Persist collapsed swimlanes to localStorage
    useEffect(() => {
        localStorage.setItem('adminCollapsedSwimlanes', JSON.stringify([...collapsedSwimlanes]));
    }, [collapsedSwimlanes]);

    // Persist epics section collapsed state
    useEffect(() => {
        localStorage.setItem('adminEpicsCollapsed', JSON.stringify(epicsCollapsed));
    }, [epicsCollapsed]);
    const [showLinksModal, setShowLinksModal] = useState(false);
    const [showAllTasksModal, setShowAllTasksModal] = useState(false);
    const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
    const [createTaskSwimlaneId, setCreateTaskSwimlaneId] = useState(null);
    const [draggedTask, setDraggedTask] = useState(null);
    // ⛔ THE REF, NOT THE STATE, FEEDS THE DROP - the touch path calls
    // onDragStart and onDrop in the same tick, so the closure's draggedTask is
    // one render stale there. See Dashboard.js's twin comment.
    const draggedTaskRef = useRef(null);
    const updateInProgressRef = useRef(false);
    const [currentView, setCurrentView] = useState(() => {
        return localStorage.getItem('adminClientView') || 'kanban';
    });
    const [timelineData, setTimelineData] = useState(null);
    const [loadingTimeline, setLoadingTimeline] = useState(false);
    // Full-screen Gantt: page chrome hides, progress bar + chart fill the
    // viewport. Deliberately NOT persisted - reloading into full screen with
    // no visible way back is a trap (Bill, 2026-08-21).
    const [ganttFullScreen, setGanttFullScreen] = useState(false);
    const [kickOffDate, setKickOffDate] = useState(null);
    const [shiftProgress, setShiftProgress] = useState(null); // { active: true } while the one server call is in flight
    // Interrupted-shift recovery (PROJECT-SHIFT plan §9.1, D13/D14). A stored
    // entry belongs to ONE shift: Resume re-sends it verbatim, a new shift is
    // refused while one exists, and Dismiss hides the banner WITHOUT clearing
    // the key - clearing it on a half-moved plan re-opens the double-shift.
    const [pendingShift, setPendingShift] = useState(null); // { shiftId, deltaDays }
    const [pendingShiftDismissed, setPendingShiftDismissed] = useState(false);
    const [shiftSummary, setShiftSummary] = useState(null); // the 200 body - drives the summary modal
    const [risks, setRisks] = useState([]);
    const [loadingRisks, setLoadingRisks] = useState(false);
    const [risksFetched, setRisksFetched] = useState(false);
    const [selectedRisk, setSelectedRisk] = useState(null);
    const [projectClients, setProjectClients] = useState([]);
    const [issues, setIssues] = useState([]);
    const [loadingIssues, setLoadingIssues] = useState(false);
    const [issuesFetched, setIssuesFetched] = useState(false);
    const [risksIssuesCollapsed, setRisksIssuesCollapsed] = useState(() => {
        const saved = localStorage.getItem('adminRisksIssuesCollapsed');
        return saved ? new Set(JSON.parse(saved)) : new Set();
    });
    const [showRiskCreate, setShowRiskCreate] = useState(false);
    const [showIssueCreate, setShowIssueCreate] = useState(false);
    const [adminTeamMembers, setAdminTeamMembers] = useState([]);

    // Add-in attach/detach state
    const [availablePoolAddins, setAvailablePoolAddins] = useState([]);
    const [showAddinDropdown, setShowAddinDropdown] = useState(false);
    const [attachingAddin, setAttachingAddin] = useState(null);
    const [detachingAddin, setDetachingAddin] = useState(null);
    const [attachReport, setAttachReport] = useState(null);
    const [showAttachReport, setShowAttachReport] = useState(false);
    const addinDropdownRef = useRef(null);

    // Combined members for Issues - includes both project members (admins) and DynamoDB clients
    const issueAssignees = useMemo(() => {
        const combined = [];
        const seenEmails = new Set();

        // Add admin team members first
        (adminTeamMembers || []).forEach(m => {
            const email = m.email || m.username;
            if (email && !seenEmails.has(email)) {
                seenEmails.add(email);
                combined.push({
                    email: email,
                    name: m.name || m.username,
                    leadId: m.leadId,
                    group: 'admin'
                });
            }
        });

        // Add project members (clients)
        (data?.members || []).forEach(m => {
            if (m.name?.toLowerCase() === 'admin' || m.username?.toLowerCase() === 'admin') return;
            const email = m.email || `${m.username}@system`;
            if (!seenEmails.has(email)) {
                seenEmails.add(email);
                combined.push({
                    email: email,
                    name: m.name || m.username,
                    leadId: m.leadId,
                    group: 'client'
                });
            }
        });

        // Add DynamoDB clients
        (projectClients || []).forEach(c => {
            if (c.email && !seenEmails.has(c.email)) {
                seenEmails.add(c.email);
                combined.push({
                    email: c.email,
                    name: c.name,
                    leadId: c.leadId,
                    group: 'client'
                });
            }
        });

        return combined;
    }, [adminTeamMembers, data?.members, projectClients]);

    // Attached add-ins derived from project record
    const attachedAddins = useMemo(() => {
        return (data?.project?.addins || []).filter(a => a.attached);
    }, [data?.project?.addins]);

    // Fetch compatible pool add-ins for the attach dropdown
    const fetchPoolAddins = useCallback(async () => {
        const projectSize = data?.project?.size;
        if (!projectSize) return;
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/templates/pool?size=${encodeURIComponent(projectSize)}`
            );
            if (response.ok) {
                const result = await response.json();
                setAvailablePoolAddins(result.pool || []);
            }
        } catch (err) {
            console.error('Failed to fetch pool add-ins:', err);
        }
    }, [data?.project?.size]);

    // Attach a pool add-in to the client project
    const handleAttachAddin = useCallback(async (addin) => {
        const projectId = data?.project?.projectId;
        if (!projectId) return;

        setAttachingAddin(addin.templateId);
        setShowAddinDropdown(false);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/project/${encodeURIComponent(projectId)}/attach-addin`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ addinId: addin.templateId })
                }
            );
            if (response.ok) {
                const result = await response.json();
                setAttachReport(result);
                setShowAttachReport(true);
            } else {
                const errorData = await response.json().catch(() => ({}));
                alert(`Failed to attach add-in: ${errorData.error || response.statusText}`);
            }
        } catch (err) {
            console.error('Failed to attach add-in:', err);
            alert(`Failed to attach add-in: ${err.message}`);
        } finally {
            setAttachingAddin(null);
        }
    }, [data?.project?.projectId]);

    // Ref to prevent duplicate fetches
    const fetchingRef = useRef(false);

    const fetchClientDashboard = useCallback(async (retryCount = 0) => {
        const token = localStorage.getItem('adminToken');
        if (!token) {
            navigate('/admin/login');
            return;
        }

        // Skip if already fetching (prevents rate limiting from duplicate WebSocket triggers)
        if (fetchingRef.current) {
            console.log('Skipping fetch - already in progress');
            return;
        }
        fetchingRef.current = true;

        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/client-dashboard/${leadId}`
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to fetch client dashboard');
            }

            const result = await response.json();
            setData(result);
            setError(null);
            fetchingRef.current = false;
            setLoading(false);
        } catch (err) {
            console.error('Error fetching client dashboard:', err);
            // Retry up to 2 times for network errors
            if (retryCount < 2 && (err.message.includes('NetworkError') || err.message.includes('fetch'))) {
                console.log(`Retrying fetch (attempt ${retryCount + 2})...`);
                fetchingRef.current = false; // Allow retry to proceed
                setTimeout(() => fetchClientDashboard(retryCount + 1), 1000 * (retryCount + 1));
                return;
            }
            setError(err.message);
            fetchingRef.current = false;
            setLoading(false);
        }
    }, [leadId, navigate]);

    // Detach a pool add-in from the client project
    const handleDetachAddin = useCallback(async (addinId, addinName) => {
        const projectId = data?.project?.projectId;
        if (!projectId) return;

        const confirmed = window.confirm(
            `Detach "${addinName}" from this project?\n\nThis will remove all tasks that were cloned from this add-in.`
        );
        if (!confirmed) return;

        setDetachingAddin(addinId);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/project/${encodeURIComponent(projectId)}/detach-addin`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ addinId })
                }
            );
            if (response.ok) {
                await fetchClientDashboard();
            } else {
                const errorData = await response.json().catch(() => ({}));
                alert(`Failed to detach add-in: ${errorData.error || response.statusText}`);
            }
        } catch (err) {
            console.error('Failed to detach add-in:', err);
            alert(`Failed to detach add-in: ${err.message}`);
        } finally {
            setDetachingAddin(null);
        }
    }, [data?.project?.projectId, fetchClientDashboard]);

    // Close attach report modal and refresh data
    const handleCloseAttachReport = useCallback(() => {
        setShowAttachReport(false);
        setAttachReport(null);
        fetchClientDashboard();
    }, [fetchClientDashboard]);

    // Close addin dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (addinDropdownRef.current && !addinDropdownRef.current.contains(e.target)) {
                setShowAddinDropdown(false);
            }
        };
        if (showAddinDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showAddinDropdown]);

    // Fetch pool add-ins when dropdown is opened
    useEffect(() => {
        if (showAddinDropdown) {
            fetchPoolAddins();
        }
    }, [showAddinDropdown, fetchPoolAddins]);

    // Operation queue for optimistic UI
    const pendingOpsRef = useRef(0);

    const handleOperationError = useCallback((operation, error) => {
        const taskName = operation.payload?.taskName || `Task ${operation.payload?.taskId || 'unknown'}`;
        alert(
            `Operation failed: ${operation.type}\n\n` +
            `Task: ${taskName}\n\n` +
            `Error: ${error.message}\n\n` +
            `All pending changes have been rolled back.`
        );
    }, []);

    const refreshTimeoutRef = useRef(null);

    const { pendingOperations, enqueue, clearCompleted } = useOperationQueue({
        onError: handleOperationError,
        onComplete: (op) => {
            // Clear completed operations immediately to prevent accumulation
            clearCompleted();
            // Refresh data after operation completes (WebSocket refresh is skipped during pending ops)
            fetchClientDashboard();
        }
    });

    // Build weekendWork map from tasks' weekendDatesWorked fields
    const weekendWork = useMemo(() => {
        const map = {};
        for (const task of (data?.tasks || [])) {
            if (task.weekendDatesWorked && Object.keys(task.weekendDatesWorked).length > 0) {
                map[task.taskId] = task.weekendDatesWorked;
            }
        }
        return map;
    }, [data?.tasks]);

    // Server state - current state from API
    const serverState = useMemo(() => ({
        subtasks: (data?.tasks || []).filter(t => t.mainTaskId),
        links: data?.links || [],
        weekendWork,
        kickOffDate
    }), [data?.tasks, data?.links, weekendWork, kickOffDate]);

    // Optimistic state - server state with pending operations applied
    const optimisticState = useMemo(() =>
        applyPendingOperations(serverState, pendingOperations),
        [serverState, pendingOperations]
    );

    // Build blocked tasks map from issues and dependencies
    const subtasksForBlocking = useMemo(
        () => optimisticState.subtasks || [],
        [optimisticState.subtasks]
    );
    const mainTasksForBlocking = useMemo(
        () => (data?.tasks || []).filter(t => !t.mainTaskId),
        [data?.tasks]
    );
    const blockedTasksMap = useMemo(
        () => buildBlockedTasksMap(issues, optimisticState.links, subtasksForBlocking, mainTasksForBlocking),
        [issues, optimisticState.links, subtasksForBlocking, mainTasksForBlocking]
    );

    // The socket, ping and reconnect timers are owned by useProjectWebSocket.
    // This debounce is ours - it belongs to the message handler, not the
    // connection - so it is still declared and cleared here.
    const refreshDebounceRef = useRef(null);
    const fetchRisksRef = useRef(null);

    // Keep pendingOpsRef in sync (count non-completed operations only)
    useEffect(() => {
        pendingOpsRef.current = pendingOperations.filter(op => !op.completed).length;
    }, [pendingOperations]);

    // Helper to update tasks within data state
    const setTasks = useCallback((updater) => {
        setData(prevData => {
            if (!prevData) return prevData;
            const newTasks = typeof updater === 'function' ? updater(prevData.tasks || []) : updater;
            return { ...prevData, tasks: newTasks };
        });
    }, []);

    // Task operations hook
    const {
        selectedTaskDetail,
        setSelectedTaskDetail,
        loadingTaskDetail,
        savingChanges,
        savingField,
        creatingTask,
        creatingSubtask,
        fetchTaskDetail,
        handleSaveChanges,
        handleDeleteTask,
        handleCreateTask,
        handleCreateSubtask,
        handleDrop
    } = useAdminTaskOperations(leadId, data?.project, setTasks, fetchClientDashboard);

    // Comments hook
    const {
        addingComment,
        editingComment,
        deletingComment,
        handleAddComment,
        handleEditComment,
        handleDeleteComment
    } = useAdminComments(data?.project?.projectId, fetchTaskDetail, leadId);

    // Closure comment hook for mandatory comments when closing subtasks
    const {
        closureModal,
        isSubmittingClosure,
        requestClosure,
        confirmClosure,
        cancelClosure
    } = useClosureComment(handleAddComment);

    // Fetch timeline data for Gantt view
    const fetchTimelineData = useCallback(async () => {
        const token = localStorage.getItem('adminToken');
        if (!token) return;

        setLoadingTimeline(true);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/timeline/${leadId}`
            );

            if (response.ok) {
                const data = await response.json();
                setTimelineData(data);
            }
        } catch (err) {
            console.error('Error fetching timeline data:', err);
        } finally {
            setLoadingTimeline(false);
        }
    }, [leadId]);

    // Fetch risks from DynamoDB
    const fetchRisks = useCallback(async (showLoading = true) => {
        const token = localStorage.getItem('adminToken');
        if (!token) return;

        if (showLoading) setLoadingRisks(true);
        try {
            // Fetch risks and project clients in parallel
            const [risksResponse, clientsResponse] = await Promise.all([
                apiFetch(`${process.env.REACT_APP_API_URL}/admin/risks/${leadId}`),
                apiFetch(`${process.env.REACT_APP_API_URL}/admin/project-clients/${leadId}`)
            ]);

            if (risksResponse.ok) {
                const result = await risksResponse.json();
                setRisks(result.risks || []);
                setRisksFetched(true);
            }

            if (clientsResponse.ok) {
                const clientsResult = await clientsResponse.json();
                setProjectClients(clientsResult.clients || []);
            }
        } catch (err) {
            console.error('Error fetching risks:', err);
        } finally {
            if (showLoading) setLoadingRisks(false);
        }
    }, [leadId]);

    // Keep fetchRisksRef in sync for WebSocket handler
    useEffect(() => {
        fetchRisksRef.current = fetchRisks;
    }, [fetchRisks]);

    // Fetch issues from DynamoDB
    const fetchIssuesRef = useRef(null);
    const fetchIssues = useCallback(async (showLoading = true) => {
        const token = localStorage.getItem('adminToken');
        if (!token) return;

        if (showLoading) setLoadingIssues(true);
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/issues/${leadId}`);
            if (response.ok) {
                const result = await response.json();
                setIssues(result.issues || []);
                setIssuesFetched(true);
            }
        } catch (err) {
            console.error('Error fetching issues:', err);
        } finally {
            if (showLoading) setLoadingIssues(false);
        }
    }, [leadId]);

    // Keep fetchIssuesRef in sync for WebSocket handler
    useEffect(() => {
        fetchIssuesRef.current = fetchIssues;
    }, [fetchIssues]);

    // Persist risks/issues collapse state to localStorage
    useEffect(() => {
        localStorage.setItem('adminRisksIssuesCollapsed', JSON.stringify([...risksIssuesCollapsed]));
    }, [risksIssuesCollapsed]);

    // Toggle section collapse
    const toggleRisksIssuesSection = (section) => {
        setRisksIssuesCollapsed(prev => {
            const newSet = new Set(prev);
            if (newSet.has(section)) {
                newSet.delete(section);
            } else {
                newSet.add(section);
            }
            return newSet;
        });
    };

    // Save subtask order to DynamoDB (queued) - updates task order fields
    const saveSubtaskOrder = useCallback((parentTaskId, order) => {
        if (!data?.project?.projectId) return;

        enqueue({
            type: 'SUBTASK_ORDER',
            payload: { parentTaskId, order, taskName: `Reorder ${parentTaskId}` },
            apiCall: async () => {
                const response = await apiFetch(
                    `${process.env.REACT_APP_API_URL}/admin/subtask-order/${data.project.projectId}`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ mainTaskId: parentTaskId, subtaskOrder: order })
                    }
                );
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || `HTTP ${response.status}`);
                }
                // WebSocket refresh will update task order fields
            }
        });
    }, [data?.project?.projectId, enqueue]);

    // Save main task order to DynamoDB (queued) - updates task order fields
    const saveMainTaskOrder = useCallback((epicId, order) => {
        if (!data?.project?.projectId) return;

        // order arrives as ["projectId:taskId", ...] composite keys from GanttChart
        // Extract just the taskIds for the API call
        const mainTaskOrder = order.map(key => {
            const parts = key.split(':');
            return parts.length > 1 ? parts.slice(1).join(':') : key;
        });

        enqueue({
            type: 'MAIN_TASK_ORDER',
            payload: { epicId, order: mainTaskOrder, taskName: 'Reorder main tasks' },
            apiCall: async () => {
                const response = await apiFetch(
                    `${process.env.REACT_APP_API_URL}/admin/main-task-order/${data.project.projectId}`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ epicId, mainTaskOrder })
                    }
                );
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || `HTTP ${response.status}`);
                }
            }
        });
    }, [data?.project?.projectId, enqueue]);

    // Save weekend work setting to DynamoDB (queued) - updates task weekendDatesWorked field
    // Accepts: (subtaskId, dateKey, worked) for single date
    // Or: (subtaskId, updates) where updates is [{ dateKey, worked }, ...]
    const saveWeekendWork = useCallback((subtaskId, dateKeyOrUpdates, worked) => {
        if (!data?.project?.projectId) return;

        const strId = String(subtaskId);
        const updates = Array.isArray(dateKeyOrUpdates)
            ? dateKeyOrUpdates
            : [{ dateKey: dateKeyOrUpdates, worked }];
        const taskName = updates.length > 1
            ? `Weekend ${updates.map(u => u.dateKey).join(', ')}`
            : `Weekend ${updates[0].dateKey}`;

        enqueue({
            type: 'WEEKEND_WORK',
            payload: { subtaskId: strId, updates, taskName },
            apiCall: async () => {
                const response = await apiFetch(
                    `${process.env.REACT_APP_API_URL}/admin/weekend-work/${data.project.projectId}`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ subtaskId: strId, updates })
                    }
                );
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || `HTTP ${response.status}`);
                }
                // WebSocket refresh will update task weekendDatesWorked field
            }
        });
    }, [data?.project?.projectId, enqueue]);

    // Fetch kick-off date from DynamoDB
    const fetchKickOffDate = useCallback(async () => {
        if (!data?.project?.projectId) return;

        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/kickoff/${data.project.projectId}`
            );

            if (response.ok) {
                const result = await response.json();
                setKickOffDate(result.kickOffDate || null);
            }
        } catch (err) {
            console.error('Error fetching kick-off date:', err);
        }
    }, [data?.project?.projectId]);

    // Save kick-off date to DynamoDB (queued)
    const saveKickOffDate = useCallback((newDate) => {
        if (!data?.project?.projectId) return;

        enqueue({
            type: 'KICKOFF_DATE',
            payload: { date: newDate, taskName: 'Kick-off date' },
            apiCall: async () => {
                const response = await apiFetch(
                    `${process.env.REACT_APP_API_URL}/admin/kickoff/${data.project.projectId}`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ kickOffDate: newDate })
                    }
                );
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || `HTTP ${response.status}`);
                }
                // Update local state on success
                setKickOffDate(newDate);
            }
        });
    }, [data?.project?.projectId, enqueue]);

    // Interrupted-shift persistence (PROJECT-SHIFT plan §9.1). One key per
    // project; the value is the WHOLE shift - id and delta - so Resume re-sends
    // it verbatim.
    const SHIFT_KEY = useCallback(pid => `adminProjectShiftPending:${pid}`, []);
    const readPendingShift = useCallback(() => {
        const pid = data?.project?.projectId;
        if (!pid) return null;
        try {
            const raw = localStorage.getItem(SHIFT_KEY(pid));
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            return null;
        }
    }, [data?.project?.projectId, SHIFT_KEY]);

    // On load, an unfinished shift is offered for Resume (D13).
    useEffect(() => {
        setPendingShift(readPendingShift());
        setPendingShiftDismissed(false);
    }, [readPendingShift]);

    // Handle project date shift - ONE server call moves the whole plan
    // (PROJECT-SHIFT plan §9.1). The 45-request loop, the 3-second sleep and
    // the client-side verification block are gone: the server reads fresh
    // state, asserts the FS invariant, moves the kick-off with the plan and
    // reports what it did. There is no client-side comparison left to get
    // wrong.
    const runProjectShift = useCallback(async (deltaDays, shiftId, isResume) => {
        const projectId = data?.project?.projectId;
        if (!projectId) return;

        // D12/R4: persisted BEFORE the request leaves, so a response that
        // never arrives still leaves something to resume with.
        localStorage.setItem(SHIFT_KEY(projectId), JSON.stringify({ shiftId, deltaDays }));
        setPendingShift({ shiftId, deltaDays });

        // Closing the tab mid-request abandons a write the server may be
        // partway through - warn. The stored id is what makes it recoverable.
        const warnUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', warnUnload);

        setShiftProgress({ active: true });
        let res;
        let body;
        try {
            res = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/project-shift/${encodeURIComponent(projectId)}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ deltaDays, shiftId })
                }
            );
            body = await res.json();
        } catch (err) {
            // Network failure, dropped connection, or a non-JSON body - the
            // shift may still be running server-side, and the id is already
            // stored, so this is the recovery path working as designed (R4).
            alert('The request failed. The shift may have partly applied - use the Resume banner to finish it.');
            return; // the stored key is NOT cleared
        } finally {
            // V1: the overlay comes down on EVERY path - without this a thrown
            // fetch leaves the screen locked behind it for good.
            setShiftProgress(null);
            window.removeEventListener('beforeunload', warnUnload);
        }

        // Cleared on success, and on a refusal that proves THIS SHIFT has
        // never written anything (shiftWroteNothing is SHIFT-scoped - plan §6,
        // W1). `!isResume` is belt and braces: a Resume exists only because an
        // earlier attempt may have written, so its recovery key is never
        // discarded on a failed request, whatever the flag says.
        if (res.ok || (body.shiftWroteNothing === true && !isResume)) {
            localStorage.removeItem(SHIFT_KEY(projectId));
            setPendingShift(null);
        }

        if (!res.ok) {
            let msg = body.error || `Project shift failed: HTTP ${res.status}`;
            if (body.shiftWroteNothing === false) {
                msg += '\n\nUse the Resume banner to finish the interrupted shift - do not start a new one.';
                setPendingShiftDismissed(false);
            }
            alert(msg);
            return;
        }

        // The server moved the kick-off with the plan; mirror the new anchor.
        if (body.kickoff) setKickOffDate(body.kickoff.after);

        // The summary modal renders what the SERVER reports - body.tasks,
        // body.moved, body.kickoff - not a client-side reconstruction.
        setShiftSummary(body);
    }, [data?.project?.projectId, SHIFT_KEY]);

    const handleProjectDateShift = useCallback(async (deltaDays) => {
        // While an entry exists a NEW shift is blocked and only Resume is
        // offered (D14). Re-using the stored id would move nothing and report
        // success; minting a fresh one alongside it is precisely the
        // double-shift.
        const pending = readPendingShift();
        if (pending) {
            setPendingShift(pending);
            setPendingShiftDismissed(false);
            alert('An interrupted shift must be resumed before a new one can start - use the Resume banner.');
            return;
        }
        if (!Number.isInteger(deltaDays) || deltaDays === 0) return;
        return runProjectShift(deltaDays, crypto.randomUUID(), false);
    }, [readPendingShift, runProjectShift]);

    const handleResumeShift = useCallback(() => {
        const pending = readPendingShift();
        if (!pending) {
            setPendingShift(null);
            return;
        }
        return runProjectShift(pending.deltaDays, pending.shiftId, true);
    }, [readPendingShift, runProjectShift]);

    // Initial data fetch
    useEffect(() => {
        fetchClientDashboard();
    }, [fetchClientDashboard]);

    // Fetch admin team members for assignment dropdowns
    useEffect(() => {
        const fetchAdminTeam = async () => {
            try {
                const response = await apiFetch(
                    `${process.env.REACT_APP_API_URL}/admin/team-members`
                );
                if (response.ok) {
                    const data = await response.json();
                    setAdminTeamMembers(data.members || []);
                }
            } catch (err) {
                console.error('Failed to fetch admin team:', err);
            }
        };
        fetchAdminTeam();
    }, []);

    // Persist view selection to localStorage
    useEffect(() => {
        localStorage.setItem('adminClientView', currentView);
    }, [currentView]);

    // Fetch timeline data when switching to gantt or resource view
    useEffect(() => {
        if ((currentView === 'gantt' || currentView === 'resource') && !timelineData && !loadingTimeline) {
            fetchTimelineData();
        }
    }, [currentView, timelineData, loadingTimeline, fetchTimelineData]);

    // Fetch kick-off date when switching to gantt view
    // Also trigger WebSocket refresh for connected clients
    useEffect(() => {
        if (currentView === 'gantt' && data?.project?.projectId) {
            fetchKickOffDate();
            // Trigger WebSocket refresh for connected clients
            apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/batch-signal/${leadId}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ signal: 'project_updated' })
                }
            ).catch(e => console.warn('Failed to send gantt refresh signal:', e));
        }
    }, [currentView, data?.project?.projectId, fetchKickOffDate, leadId]);

    // Fetch issues on initial load (needed for blockedTasksMap in Gantt/Epics)
    useEffect(() => {
        if (!issuesFetched && !loadingIssues) {
            fetchIssues();
        }
    }, [issuesFetched, loadingIssues, fetchIssues]);

    // Fetch risks when switching to risks view
    useEffect(() => {
        if (currentView === 'risks') {
            if (!risksFetched && !loadingRisks) {
                fetchRisks();
            }
        }
    }, [currentView, risksFetched, loadingRisks, fetchRisks]);

    // Listen for task click events from Project Links modal
    useEffect(() => {
        const handleOpenTaskFromLinks = (event) => {
            const { taskId } = event.detail;
            if (taskId) {
                fetchTaskDetail(taskId);
            }
        };
        window.addEventListener('openTaskFromLinks', handleOpenTaskFromLinks);
        return () => window.removeEventListener('openTaskFromLinks', handleOpenTaskFromLinks);
    }, [fetchTaskDetail]);

    // WebSocket connection. Plumbing lives in useProjectWebSocket; this is the
    // admin portal riding a CLIENT's leadId, so it sends the ADMIN token - the
    // authorizer allows an admin to open any client's channel.
    const handleWebSocketMessage = useCallback((message) => {
        console.log('Admin WebSocket message:', message);

        if (message.type === 'project_updated') {
            // Skip refresh if from our own admin task update - we handle that via onComplete
            if (message.source === 'admin_task_update') {
                console.log('Skipping WebSocket refresh - from our own update');
                return;
            }
            // Skip refresh if operations are pending (prevents double-shift on optimistic UI)
            if (pendingOpsRef.current > 0) {
                console.log('Skipping WebSocket refresh - operations pending');
                return;
            }
            // Debounce refresh to avoid multiple simultaneous fetches
            if (refreshDebounceRef.current) {
                clearTimeout(refreshDebounceRef.current);
            }
            refreshDebounceRef.current = setTimeout(() => {
                fetchClientDashboard();
                fetchKickOffDate();
            }, 500);
        }

        if (message.type === 'risks_updated') {
            console.log('Risks updated via WebSocket');
            // Silent refresh (no loading spinner)
            if (fetchRisksRef.current) {
                fetchRisksRef.current(false);
            }
        }

        if (message.type === 'issues_updated') {
            console.log('Issues updated via WebSocket');
            // Silent refresh (no loading spinner)
            if (fetchIssuesRef.current) {
                fetchIssuesRef.current(false);
            }
        }
    }, [fetchClientDashboard, fetchKickOffDate]);

    useProjectWebSocket({
        leadId,
        timestamp: data?.lead?.timestamp,
        // getToken, not a captured token: reconnects hours in must carry the
        // CURRENT token or the authorizer refuses them forever (BUG-030)
        getToken: () => localStorage.getItem('adminToken'),
        onMessage: handleWebSocketMessage,
        enabled: !!data?.lead?.timestamp
    });

    // The debounce timer is owned here, not by the hook, so it must still be
    // cleared on unmount
    useEffect(() => {
        return () => {
            if (refreshDebounceRef.current) {
                clearTimeout(refreshDebounceRef.current);
            }
        };
    }, []);

    // Calculate heatmap color (amber -> yellow -> green)
    const getProgressColor = (percent) => {
        if (percent <= 50) {
            const ratio = percent / 50;
            const r = Math.round(251 - (251 - 250) * ratio);
            const g = Math.round(146 + (204 - 146) * ratio);
            const b = Math.round(60 - (60 - 21) * ratio);
            return `rgb(${r}, ${g}, ${b})`;
        } else {
            const ratio = (percent - 50) / 50;
            const r = Math.round(250 - (250 - 34) * ratio);
            const g = Math.round(204 - (204 - 197) * ratio);
            const b = Math.round(21 + (94 - 21) * ratio);
            return `rgb(${r}, ${g}, ${b})`;
        }
    };

    // Queued Gantt callbacks
    const handleTaskUpdate = useCallback((taskId, { start, end, isSubtask, allowBeforeKickoff }) => {
        const subtask = serverState.subtasks.find(s => s.taskId === taskId);
        return enqueue({
            type: 'TASK_UPDATE',
            payload: {
                taskId,
                start,
                end,
                isSubtask,
                taskName: subtask?.subject || `Task ${taskId}`
            },
            apiCall: async () => {
                const response = await apiFetch(
                    `${process.env.REACT_APP_API_URL}/admin/task/${encodeURIComponent(taskId)}`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            updates: {
                                due_date: end,
                                custom_attributes_values: { 'Start Date': start }
                            },
                            isSubtask: isSubtask,
                            leadId,
                            allowBeforeKickoff: allowBeforeKickoff
                        })
                    }
                );
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || `HTTP ${response.status}`);
                }
                // Update local data (admin has no WebSocket refresh)
                setData(prevData => {
                    if (!prevData?.tasks) return prevData;
                    const updatedTasks = prevData.tasks.map(task => {
                        if (task.taskId === taskId) {
                            return {
                                ...task,
                                start,
                                due_date: end,
                                custom_attributes_values: {
                                    ...task.custom_attributes_values,
                                    'Start Date': start
                                }
                            };
                        }
                        return task;
                    });
                    return { ...prevData, tasks: updatedTasks };
                });
            }
        });
    }, [serverState.subtasks, enqueue, leadId]);

    const handleDependencyCreate = useCallback(({ source, target, type }) => {
        const newLink = { source, target, type: type || 'FS' };
        enqueue({
            type: 'DEPENDENCY_CREATE',
            payload: {
                sourceId: source,
                targetId: target,
                link: newLink,
                taskName: `Dependency ${source} -> ${target}`
            },
            apiCall: async () => {
                const response = await apiFetch(
                    `${process.env.REACT_APP_API_URL}/admin/dependency`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ source, target, type: type || 'FS', leadId })
                    }
                );
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || `HTTP ${response.status}`);
                }
                // Update local data (admin has no WebSocket refresh)
                setData(prevData => {
                    if (!prevData) return prevData;
                    return {
                        ...prevData,
                        links: [...(prevData.links || []), newLink]
                    };
                });
            }
        });
    }, [enqueue, leadId]);

    const handleDependencyDelete = useCallback((link) => {
        enqueue({
            type: 'DEPENDENCY_DELETE',
            payload: {
                sourceId: link.source,
                targetId: link.target,
                taskName: `Dependency ${link.source} -> ${link.target}`
            },
            apiCall: async () => {
                const response = await apiFetch(
                    `${process.env.REACT_APP_API_URL}/admin/dependency`,
                    {
                        method: 'DELETE',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ source: link.source, target: link.target, leadId })
                    }
                );
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || `HTTP ${response.status}`);
                }
                // Update local data (admin has no WebSocket refresh)
                setData(prevData => {
                    if (!prevData) return prevData;
                    return {
                        ...prevData,
                        links: (prevData.links || []).filter(l =>
                            !(String(l.source) === String(link.source) &&
                              String(l.target) === String(link.target))
                        )
                    };
                });
            }
        });
    }, [enqueue, leadId]);

    const handleDependencyColorChange = useCallback((link, newColor) => {
        if (!data?.project?.projectId) return;
        const projectId = data.project.projectId;

        enqueue({
            type: 'DEPENDENCY_COLOR_CHANGE',
            payload: {
                sourceId: link.source,
                targetId: link.target,
                color: newColor,
                taskName: `Dependency colour ${link.source} -> ${link.target}`
            },
            apiCall: async () => {
                const response = await apiFetch(
                    `${process.env.REACT_APP_API_URL}/admin/dependency-color`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            projectId,
                            sourceId: link.source,
                            targetId: link.target,
                            color: newColor,
                            leadId
                        })
                    }
                );
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || `HTTP ${response.status}`);
                }
                // Update local data (admin has no WebSocket refresh)
                setData(prevData => {
                    if (!prevData) return prevData;
                    return {
                        ...prevData,
                        links: (prevData.links || []).map(l =>
                            (String(l.source) === String(link.source) &&
                             String(l.target) === String(link.target))
                                ? { ...l, color: newColor }
                                : l
                        )
                    };
                });
            }
        });
    }, [data?.project?.projectId, enqueue, leadId]);

    const handleStatusChange = useCallback((taskId, statusId) => {
        const subtask = serverState.subtasks.find(s => s.taskId === taskId);
        enqueue({
            type: 'STATUS_CHANGE',
            payload: {
                taskId,
                statusId,
                taskName: subtask?.subject || `Task ${taskId}`
            },
            apiCall: async () => {
                const response = await apiFetch(
                    `${process.env.REACT_APP_API_URL}/admin/task/${encodeURIComponent(taskId)}`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            updates: { status: statusId },
                            isSubtask: true,
                            leadId
                        })
                    }
                );
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || `HTTP ${response.status}`);
                }
                // Trigger WebSocket refresh for connected clients
                apiFetch(
                    `${process.env.REACT_APP_API_URL}/admin/batch-signal/${leadId}`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ signal: 'project_updated' })
                    }
                ).catch(e => console.warn('Failed to send status change signal:', e));
            }
        });
    }, [serverState.subtasks, enqueue, leadId]);

    // Parent task move (complex - keeps existing implementation with its own error handling)
    const handleParentTaskMove = useCallback(async (parentTaskId, deltaDays, subtasksToMove, allowBeforeKickoff) => {
        if (deltaDays === 0 || subtasksToMove.length === 0) return;

        // Weekend-alignment confirm now lives in GanttChart BEFORE overrides
        // are applied - a cancelled move never reaches this handler

        // Interrupted-move recovery, same terms as the project shift (plan
        // §8.3). The key includes the MAIN TASK id - two parents interrupted
        // on the same project must not share one entry. An existing entry is
        // resumed with ITS stored id and delta, never replaced by the new
        // drag: a fresh id over a half-moved subtree is the double-shift.
        const pmKey = `adminParentMovePending:${data?.project?.projectId}:${parentTaskId}`;
        let storedMove = null;
        try {
            storedMove = JSON.parse(localStorage.getItem(pmKey) || 'null');
        } catch (e) {
            storedMove = null;
        }
        const isResume = storedMove !== null;
        const moveShiftId = isResume ? storedMove.shiftId : crypto.randomUUID();
        const moveDelta = isResume ? storedMove.deltaDays : deltaDays;
        if (isResume && moveDelta !== deltaDays) {
            alert('An interrupted move of this task is being finished first - repeat your move once it completes.');
        }
        // Persisted BEFORE the request leaves (D12/R4).
        localStorage.setItem(pmKey, JSON.stringify({ shiftId: moveShiftId, deltaDays: moveDelta }));

        // Enqueue a single PARENT_MOVE operation for optimistic UI
        const affectedSubtaskIds = subtasksToMove.map(s => s.originalId || s.taskId);
        return enqueue({
            type: 'PARENT_MOVE',
            payload: {
                parentId: parentTaskId,
                deltaDays: moveDelta,
                affectedSubtaskIds,
                taskName: `Move parent task ${parentTaskId}`
            },
            apiCall: async () => {
                const formatDate = (d) => d ? d.toISOString().split('T')[0] : null;

                // Signal batch start to client WebSocket (suppresses individual update refreshes)
                try {
                    await apiFetch(
                        `${process.env.REACT_APP_API_URL}/admin/batch-signal/${leadId}`,
                        {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ signal: 'batch_start' })
                        }
                    );
                } catch (e) {
                    console.warn('Failed to send batch_start signal:', e);
                }

                try {
                    // ONE server call shifts the whole main task from FRESH
                    // server state (2026-07-29): no per-subtask HTTP loop, no
                    // stale-closure dates, rapid successive moves compose, and
                    // the FS check runs once on the final group state
                    const response = await apiFetch(
                        `${process.env.REACT_APP_API_URL}/admin/parent-move/${encodeURIComponent(data.project.projectId)}/${encodeURIComponent(parentTaskId)}`,
                        {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ deltaDays: moveDelta, allowBeforeKickoff, shiftId: moveShiftId })
                        }
                    );
                    if (!response.ok) {
                        const errorData = await response.json().catch(() => ({}));
                        // Cleared only when the server proves this MOVE (not
                        // just this request) never wrote - and never on a
                        // resume, whose earlier attempt may have (§6, W1).
                        if (errorData.shiftWroteNothing === true && !isResume) {
                            localStorage.removeItem(pmKey);
                        }
                        throw new Error(errorData.error || `Parent move failed: HTTP ${response.status}`);
                    }
                    localStorage.removeItem(pmKey);

                    // No second weekend-work call: parentMove re-keys the moved
                    // subtree's weekend maps in the same write as its dates
                    // (plan §8.2). The old project-wide call corrupted every
                    // unmoved task's marks on every full-week move (ISS-038).

                    // Update local data with new dates (admin has no WebSocket refresh)
                    setData(prevData => {
                        if (!prevData?.tasks) return prevData;
                        const updatedTasks = prevData.tasks.map(task => {
                            if (affectedSubtaskIds.includes(task.taskId)) {
                                const origStart = task.startDate || task.custom_attributes_values?.['Start Date'];
                                const origEnd = task.dueDate;
                                const newStart = origStart ? formatDate(new Date(new Date(origStart).getTime() + moveDelta * 24 * 60 * 60 * 1000)) : null;
                                const newEnd = origEnd ? formatDate(new Date(new Date(origEnd).getTime() + moveDelta * 24 * 60 * 60 * 1000)) : null;
                                return {
                                    ...task,
                                    start: newStart,
                                    due_date: newEnd,
                                    custom_attributes_values: {
                                        ...task.custom_attributes_values,
                                        'Start Date': newStart
                                    }
                                };
                            }
                            return task;
                        });
                        return { ...prevData, tasks: updatedTasks };
                    });
                } finally {
                    // Always signal batch end to client WebSocket (triggers single refresh)
                    try {
                        await apiFetch(
                            `${process.env.REACT_APP_API_URL}/admin/batch-signal/${leadId}`,
                            {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ signal: 'batch_end' })
                            }
                        );
                    } catch (e) {
                        console.warn('Failed to send batch_end signal:', e);
                    }
                }
            }
        });
    }, [enqueue, data?.project?.projectId, serverState.subtasks, setData, leadId, fetchClientDashboard]);

    // Quick assign handler for Epics table
    const handleQuickAssign = useCallback(async (taskId, userEmail, isSubtask) => {
        try {
            // Look up the name from members or adminTeamMembers (email is unique identifier)
            const allMembers = [...(data?.members || []), ...(adminTeamMembers || [])];
            const member = allMembers.find(m => m.email === userEmail);
            const assignedToName = member?.name || member?.email || userEmail;

            // Main tasks hold a LIST of assignees (assignedUsers/assignedUsersNames);
            // assignedTo/assignedToName are the subtask shape. Writing the subtask
            // shape onto a main task hid the assignee from the task modal, which reads
            // the list (Bill, 2026-08-03). Clearing sends empty arrays, so no stale
            // name is left behind.
            const updates = isSubtask
                ? { assigned_to: userEmail, assignedToName }
                : {
                    assignedUsers: userEmail ? [userEmail] : [],
                    assignedUsersNames: userEmail ? [assignedToName] : [],
                    // Explicitly null on main tasks: the field is the subtask shape,
                    // and nulling it clears legacy values left by the old write path
                    assignedToName: null
                };

            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/task/${encodeURIComponent(taskId)}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        updates,
                        isSubtask: isSubtask,
                        projectId: data?.project?.projectId,
                        leadId
                    })
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update assignment');
            }

            // Refresh data
            await fetchClientDashboard();
        } catch (err) {
            console.error('Quick assign error:', err);
        }
    }, [data?.project?.projectId, data?.members, leadId, adminTeamMembers, fetchClientDashboard]);

    // Quick status change handler for Epics table
    const handleQuickStatusChange = useCallback(async (taskId, statusId) => {
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/task/${encodeURIComponent(taskId)}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        updates: { status: statusId },
                        isSubtask: true,
                        projectId: data?.project?.projectId,
                        leadId
                    })
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update status');
            }

            // Trigger WebSocket refresh for connected clients
            apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/batch-signal/${leadId}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ signal: 'project_updated' })
                }
            ).catch(e => console.warn('Failed to send status change signal:', e));

            // Refresh data
            await fetchClientDashboard();
        } catch (err) {
            console.error('Quick status change error:', err);
        }
    }, [data?.project?.projectId, fetchClientDashboard, leadId]);

    // Handler for StatusDropdown closure required
    const handleEpicsClosureRequired = useCallback((task, statusId) => {
        requestClosure(
            task.taskId,
            task.subject,
            task.isMeeting,
            statusId,
            () => handleQuickStatusChange(task.taskId, statusId)
        );
    }, [requestClosure, handleQuickStatusChange]);

    // Handler for Gantt closure required
    const handleGanttClosureRequired = useCallback((taskId, taskTitle, isMeeting, statusId) => {
        requestClosure(
            taskId,
            taskTitle,
            isMeeting,
            statusId,
            () => handleStatusChange(taskId, statusId)
        );
    }, [requestClosure, handleStatusChange]);

    // Wrapper for Kanban drop handler. Columns are epics, which no longer carry
    // a closed flag, so there is no closure check on this path - the Kanban
    // moves main tasks between columns and closure is derived from subtask
    // state. Subtask closure is still prompted from the Gantt status popup,
    // which keys off the subtask STATUS being closed.
    const handleDropWithClosureCheck = useCallback((task, statusId, cols, updateRef) => {
        handleDrop(task, statusId, cols, updateRef);
    }, [handleDrop]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading client dashboard...</p>
            </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button
            onClick={() => navigate('/admin')}
            className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
            >
            Back to Clients
            </button>
            </div>
            </div>
        );
    }

    if (!data) {
        console.warn('ClientView: data is null', { loading, error, leadId, hasToken: !!localStorage.getItem('adminToken') });
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading data...</p>
            </div>
            </div>
        );
    }

    const { project, tasks, members, lead } = data;
    const epics = project?.epics || [];

    // Get columns from project
    const columns = project?.epics || [];

    // Find team swimlane ID
    const teamSwimlane = project?.swimlanes?.find(s => s.name === 'Team');
    const teamSwimlaneId = teamSwimlane?.swimlaneId;

    // Bulk expand/collapse for the Epics section, mirroring the Gantt toolbar.
    // NOTE the inverted convention here: a key PRESENT in collapsedColumns means
    // that row is EXPANDED (the Gantt's expandedItems defaults the other way).
    // Rows appear under both groupings, so both key shapes are written:
    // status-/maintask- for the Status groups, epic-/epic-maintask- for the
    // Epic groups. Toggle rule matches the Gantt: anything expanded collapses
    // everything, otherwise expand everything.
    /**
     * Save the project's typed effort estimate (T474/D11).
     *
     * ⚠️ Its own route, on admin-gantt - which is why `template.yaml` needed an
     * Events entry for it. Without one the PUT is a 403 with no CORS headers.
     */
    const handleProjectEffortSave = async (value) => {
        setSavingProjectEffort(true);
        try {
            await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/project-effort/${data?.project?.projectId}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ effortEstimated: value })
                }
            );
            setEditingProjectEffort(false);
            await fetchClientDashboard();
        } catch (err) {
            console.error('Failed to save project effort:', err);
        } finally {
            setSavingProjectEffort(false);
        }
    };

    /**
     * The PROJECT's effort line: typed estimate, both rollups, variance,
     * unestimated count (D8/D24).
     *
     * ⛔ COMPUTED FROM THE TASK LIST, NEVER BY ADDING UP THE EPIC TOTALS ON
     * SCREEN. The Epics section drops a whole epic whose main tasks are all
     * subtask-less, and that epic's effort still counts (D22) - so the two would
     * disagree, with the project line being the wrong one.
     */
    const renderProjectEffort = () => {
        // ⚠️ `project` here is `data.project`, destructured above - the same
        // object `typed` reads two lines down, so the line cannot roll up one
        // project's tasks against another's typed estimate.
        const rows = coreMainTasks(tasks, { teamSwimlaneId, project });
        const estimates = sumEffort(rows, 'effortEstimated');
        const actuals = sumEffort(rows, 'effortActual');
        const typed = data?.project?.effortEstimated;

        return (
            <span className="flex items-center gap-1.5 text-xs text-gray-600">
                <span className="text-gray-400">|</span>
                <span title="Estimate typed on the project">Est {formatEffort(typed) ?? '-'}</span>
                <span className="text-gray-300">·</span>
                <span title="Sum of every main task's estimate">Planned {formatEffort(estimates.total)}</span>
                <span className="text-gray-300">·</span>
                <span title="Sum of every main task's actual">Act {formatEffort(actuals.total)}</span>
                <span className="text-gray-300">·</span>
                <span
                    className={isOverrun(typed, actuals.total) ? 'text-red-600 font-semibold' : ''}
                    title="Actual effort minus the estimate typed on the project"
                >
                    {varianceLabel(typed, actuals.total)}
                </span>
                {estimates.unestimated > 0 && (
                    <span
                        className="text-amber-600"
                        title="Main tasks with no estimate - the Planned total does not include them"
                    >
                        · {estimates.unestimated} unestimated
                    </span>
                )}
                <Tooltip text="Set the project's effort estimate">
                    <button
                        onClick={() => setEditingProjectEffort(!editingProjectEffort)}
                        className="text-gray-400 hover:text-primary-600 text-xs underline"
                        aria-label="Set project effort"
                    >
                        edit
                    </button>
                </Tooltip>
                {editingProjectEffort && (
                    <EffortInput
                        value={typed}
                        saving={savingProjectEffort}
                        onCommit={handleProjectEffortSave}
                    />
                )}
            </span>
        );
    };

    const toggleAllEpicRows = (type, swimlaneFilter = null) => {
        setCollapsedColumns(prev => {
            const keys = [];
            // Groups holding the matched tasks. A main-task row only renders
            // inside an expanded group, so expanding tasks while their group is
            // shut would change nothing on screen - open the ancestors too.
            const ancestorKeys = [];

            if (type === 'group') {
                columns.filter(status => !status.is_archived).forEach(status => keys.push(`status-${status.epicId}`));
                epics.forEach(epic => keys.push(`epic-${epic.epicId}`));
            } else {
                (tasks || []).filter(t => !t.mainTaskId).forEach(mainTask => {
                    const isTeamTask = mainTask.swimlaneId === teamSwimlaneId ||
                        (mainTask.swimlaneId === null && teamSwimlaneId === project?.default_swimlane);
                    if (swimlaneFilter === 'team' && !isTeamTask) return;
                    if (swimlaneFilter === 'tme' && isTeamTask) return;
                    keys.push(`maintask-${mainTask.taskId}`);
                    keys.push(`epic-maintask-${mainTask.taskId}`);
                    if (mainTask.epicId) ancestorKeys.push(`status-${mainTask.epicId}`);
                    (mainTask.epics || []).forEach(e => ancestorKeys.push(`epic-${e.id}`));
                });
            }

            const anyExpanded = keys.some(key => prev.has(key));
            const next = new Set(prev);
            keys.forEach(key => anyExpanded ? next.delete(key) : next.add(key));
            // Collapsing leaves the groups as the user had them - only the
            // expand direction needs the ancestors opened.
            if (!anyExpanded) ancestorKeys.forEach(key => next.add(key));
            return next;
        });
    };


    // Calculate progress from subtasks
    const subtasks = tasks?.filter(t => t.mainTaskId) || [];
    const totalSubtasks = subtasks.length;
    const completedSubtasks = subtasks.filter(t => t.isClosed).length;
    const progressPercent = totalSubtasks > 0 ? Math.round((completedSubtasks / totalSubtasks) * 100) : 0;

    // Blocked tooltip handlers
    const handleBlockedMouseEnter = (e, blockers) => {
        if (blockedTooltipTimeoutRef.current) clearTimeout(blockedTooltipTimeoutRef.current);
        const rect = e.currentTarget.getBoundingClientRect();
        blockedTooltipTimeoutRef.current = setTimeout(() => {
            setBlockedTooltip({
                content: blockers,
                x: rect.left + rect.width / 2,
                y: rect.top - 8
            });
        }, 200);
    };

    const handleBlockedMouseLeave = () => {
        if (blockedTooltipTimeoutRef.current) clearTimeout(blockedTooltipTimeoutRef.current);
        blockedTooltipTimeoutRef.current = setTimeout(() => {
            setBlockedTooltip(null);
        }, 100);
    };

    return (
        <div className="h-full bg-gray-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white shadow">
        {/* Back link and the client identity sit together on the left, separated
            by a spacer. The title used to live in the centred container, which
            put it a long way right of the back link (Bill, 2026-07-31). */}
        <div className="w-full px-4 py-3 flex items-center gap-4">
        <button
        onClick={() => navigate('/admin')}
        className="text-gray-600 hover:text-gray-900 flex items-center gap-2 whitespace-nowrap"
        >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to Clients
        </button>
        <div className="h-8 w-px bg-gray-300 shrink-0" />
        <div>
        <h1 className="text-2xl font-bold text-gray-900">
        {lead?.company || 'Client'} - Dashboard View
        </h1>
        <p className="text-sm text-gray-500">
        Client: {lead?.name} ({lead?.email})
        </p>
        </div>
        {/* Add-ins and the view controls share the title's row, pushed right with
            ml-auto. They each had a row of their own below (Bill, 2026-07-31). */}
        <div className="ml-auto flex items-center gap-4">
        {/* Separator between the add-ins (order-first, so they render left of
            this) and the view controls */}
        <div className="h-6 w-px bg-gray-300 shrink-0" />
        <div className="flex items-center gap-2">
        {/* View Toggle */}
        <div className="flex rounded overflow-hidden border border-gray-300">
            <button
                onClick={() => setCurrentView('kanban')}
                className={`px-3 py-1.5 text-sm font-medium ${
                    currentView === 'kanban'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
            >
                Kanban
            </button>
            <button
                onClick={() => setCurrentView('gantt')}
                className={`px-3 py-1.5 text-sm font-medium ${
                    currentView === 'gantt'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
            >
                Gantt
            </button>
            <button
                onClick={() => setCurrentView('risks')}
                className={`px-3 py-1.5 text-sm font-medium ${
                    currentView === 'risks'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
            >
                Risks/Issues
            </button>
            <button
                onClick={() => setCurrentView('resource')}
                className={`px-3 py-1.5 text-sm font-medium ${
                    currentView === 'resource'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
            >
                Resource
            </button>
        </div>
        <div className="h-6 w-px bg-gray-300 mx-2"></div>
        <button
            onClick={() => setShowAllTasksModal(true)}
            className="text-sm px-4 py-2 bg-gray-100 text-gray-600 border border-gray-300 rounded hover:bg-gray-200 flex items-center gap-2"
        >
            <FaTasks size={14} />
            All Tasks
        </button>
        <button
            onClick={() => setShowLinksModal(true)}
            className="text-sm px-4 py-2 bg-gray-100 text-gray-600 border border-gray-300 rounded hover:bg-gray-200 flex items-center gap-2"
        >
            <FaLink size={14} />
            Project Links
        </button>
        </div>

        {/* Attached add-in badges and attach button. order-first puts them left
            of the view controls while staying inside the same right-hand group. */}
        {data?.project?.size && (
            <div className="order-first flex items-center gap-2 flex-wrap">
                {attachedAddins.map(addin => (
                    <span
                        key={addin.addinId}
                        className="flex items-center gap-1 px-2 py-1 rounded-lg text-sm font-medium"
                        style={{
                            backgroundColor: `${addin.colour}20`,
                            color: addin.colour
                        }}
                    >
                        <FaPuzzlePiece className="w-3 h-3" />
                        <span>{addin.abbreviation || addin.name}</span>
                        <button
                            onClick={() => handleDetachAddin(addin.addinId, addin.name)}
                            disabled={detachingAddin === addin.addinId}
                            className="p-0.5 hover:bg-white/30 rounded"
                            title={`Detach ${addin.name}`}
                        >
                            {detachingAddin === addin.addinId ? (
                                <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                <FaTimes className="w-3 h-3" />
                            )}
                        </button>
                    </span>
                ))}

                {/* Attach add-in dropdown */}
                <div className="relative" ref={addinDropdownRef}>
                    <button
                        onClick={() => setShowAddinDropdown(!showAddinDropdown)}
                        className="flex items-center gap-1 px-2 py-1 border border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gray-400 hover:text-gray-600 text-sm"
                    >
                        <FaPlus className="w-3 h-3" />
                        <span>Attach Add-in</span>
                    </button>

                    {showAddinDropdown && (
                        <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                            <div className="p-2 border-b border-gray-100">
                                <span className="text-xs font-medium text-gray-500 uppercase">
                                    Attach Pool Add-in ({data.project.size})
                                </span>
                            </div>
                            {availablePoolAddins.length === 0 ? (
                                <div className="p-3 text-sm text-gray-500">
                                    No compatible add-ins available
                                </div>
                            ) : (
                                <div className="max-h-60 overflow-y-auto">
                                    {availablePoolAddins.map(addin => {
                                        const isAttached = attachedAddins.some(a => a.addinId === addin.templateId);
                                        const isAttaching = attachingAddin === addin.templateId;
                                        return (
                                            <button
                                                key={addin.templateId}
                                                onClick={() => handleAttachAddin(addin)}
                                                disabled={isAttached || isAttaching}
                                                className={`w-full px-3 py-2 text-left flex items-center gap-2 ${
                                                    isAttached || isAttaching
                                                        ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                                                        : 'hover:bg-gray-50'
                                                }`}
                                            >
                                                {isAttaching ? (
                                                    <svg className="animate-spin w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                ) : (
                                                    <FaPuzzlePiece
                                                        className="w-4 h-4"
                                                        style={{ color: isAttached ? '#9CA3AF' : addin.colour }}
                                                    />
                                                )}
                                                <span className="flex-1 text-sm">{addin.name}</span>
                                                {addin.abbreviation && (
                                                    <span
                                                        className="px-1.5 py-0.5 text-xs font-bold rounded"
                                                        style={{
                                                            backgroundColor: isAttached ? '#E5E7EB' : `${addin.colour}20`,
                                                            color: isAttached ? '#9CA3AF' : addin.colour
                                                        }}
                                                    >
                                                        {addin.abbreviation}
                                                    </span>
                                                )}
                                                {isAttached && (
                                                    <span className="text-xs text-gray-400">Attached</span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        )}
        </div>
        </div>
        </div>

        {/* Full-screen wrapper: display-contents (zero layout impact) until the
            Gantt full-screen toggle flips it into a viewport overlay carrying
            the progress bar and the chart. The other views' blocks sit inside
            but render null in Gantt view, so nothing else is affected. */}
        <div className={ganttFullScreen && currentView === 'gantt' ? 'fixed inset-0 z-[100] bg-white flex flex-col' : 'contents'}>
        {/* Progress Bar */}
        {totalSubtasks > 0 && (
            <div className="w-full bg-gradient-to-b from-gray-50 to-gray-200 border-t border-b border-t-gray-100 border-b-gray-400 px-4 py-2 shadow-[0_3px_5px_rgba(0,0,0,0.15)]">
            <div className="flex items-center gap-3">
            <span className="text-[13px] text-gray-500 whitespace-nowrap">Overall Project Progress ({totalSubtasks - completedSubtasks} Outstanding, {completedSubtasks} Closed Tasks)</span>
            <div className="flex-1 h-2 bg-gray-300 rounded-full overflow-hidden">
            <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                    width: `${progressPercent}%`,
                    backgroundColor: getProgressColor(progressPercent),
                    backgroundImage: 'linear-gradient(to bottom, rgba(255,255,255,0.35), rgba(0,0,0,0.12))'
                }}
            />
            </div>
            <span className="text-xs font-medium text-gray-600 whitespace-nowrap">{progressPercent}%</span>
            </div>
            </div>
        )}

        {/* Epics Section - Groups tasks by Status (hidden in Gantt and Risks views) */}
        {currentView !== 'gantt' && currentView !== 'risks' && columns && columns.length > 0 && (() => {
            // Calculate visible status count (statuses with tasks that have subtasks)
            const visibleStatusCount = columns
                .filter(status => !status.is_archived)
                .filter(status => {
                    const statusTasks = (tasks?.filter(t => !t.mainTaskId && t.epicId === status.epicId) || [])
                        .filter(mainTask => tasks?.some(t => t.mainTaskId === mainTask.taskId));
                    return statusTasks.length > 0;
                }).length;

            return (
            <div className="w-full mx-auto p-2">
            <div className="bg-white rounded-lg shadow">
            {/* Epics Header */}
            <div className="px-4 py-2 border-b border-gray-200 flex justify-between items-center">
            <div
            className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 flex-1"
            onClick={() => setEpicsCollapsed(!epicsCollapsed)}
            >
            {epicsCollapsed ? <FaChevronRight /> : <FaChevronDown />}
            <h2 className="text-lg font-semibold text-gray-900">Epics</h2>
            <span className="text-sm text-gray-500">({visibleStatusCount})</span>
            {/* T474 - the PROJECT-level effort line, and where its estimate is
                typed (D11/D13). Stops the click reaching the collapse toggle. */}
            <span onClick={(e) => e.stopPropagation()}>{renderProjectEffort()}</span>
            </div>
            <div className="flex items-center gap-2">
            {/* Bulk expand/collapse - same set as the Gantt toolbar. Replaces the
                old single Expand All button, which wrote `maintask-` keys but
                tested for `mainTask-`, so it never collapsed and never relabelled. */}
            <span className="text-sm font-medium text-gray-700">Expand/Collapse:</span>
            <div className="flex gap-1">
                <Tooltip text="Expand/Collapse all Epics (Status groups)">
                    <button
                        onClick={() => toggleAllEpicRows('group')}
                        className="px-2 py-1 text-xs font-medium rounded bg-gray-200 text-gray-600 hover:bg-gray-300"
                    >
                        Epics
                    </button>
                </Tooltip>
                <Tooltip text="Expand/Collapse all Main Tasks">
                    <button
                        onClick={() => toggleAllEpicRows('task')}
                        className="px-2 py-1 text-xs font-medium rounded bg-gray-200 text-gray-600 hover:bg-gray-300"
                    >
                        Main Tasks
                    </button>
                </Tooltip>
                <Tooltip text="Expand/Collapse T.M.E. Tasks">
                    <button
                        onClick={() => toggleAllEpicRows('task', 'tme')}
                        className="px-2 py-1 text-xs font-medium rounded bg-gray-200 text-gray-600 hover:bg-gray-300"
                    >
                        T.M.E. Tasks
                    </button>
                </Tooltip>
                <Tooltip text="Expand/Collapse Client Tasks">
                    <button
                        onClick={() => toggleAllEpicRows('task', 'team')}
                        className="px-2 py-1 text-xs font-medium rounded bg-gray-200 text-gray-600 hover:bg-gray-300"
                    >
                        Client Tasks
                    </button>
                </Tooltip>
                <Tooltip text={epicsCollapsed ? 'Expand the Epics section' : 'Collapse the Epics section'}>
                    <button
                        onClick={() => setEpicsCollapsed(!epicsCollapsed)}
                        className="px-2 py-1 text-xs font-medium rounded bg-gray-300 text-gray-700 hover:bg-gray-400"
                    >
                        {epicsCollapsed ? 'Expand All' : 'Collapse All'}
                    </button>
                </Tooltip>
            </div>
            <button
                onClick={() => setHideCompletedEpics(!hideCompletedEpics)}
                className={`flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                    hideCompletedEpics
                        ? 'border-primary-600 bg-primary-600 text-white hover:bg-primary-700'
                        : 'border-gray-300 bg-white hover:bg-gray-50 text-gray-600'
                }`}
                title={hideCompletedEpics ? 'Show completed main tasks' : 'Hide completed main tasks'}
            >
                <FaCheck className="w-3 h-3" />
                <span>Hide completed</span>
            </button>
            <button
            onClick={(e) => {
                e.stopPropagation();
                fetchClientDashboard();
            }}
            className="text-gray-600 hover:text-gray-900 p-2"
            title="Refresh data"
            >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            </button>
            </div>
            </div>

            {/* Epics Table */}
            {!epicsCollapsed && (
                <div className="overflow-x-auto">
                <table className="w-full">
                <thead className="bg-gray-50 border-b">
                <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                {/* Expand column */}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Project
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Dates
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Assigned
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sprint/Status
                </th>
                {/* T473 - effort. Admin-only by construction: this whole page is
                    the admin client view. */}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Effort
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Progress
                </th>
                </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                {columns
                    .sort((a, b) => a.order - b.order)
                    .filter((status) => {
                        // Hide archived statuses and statuses with no tasks that have subtasks
                        if (status.is_archived) return false;
                        const statusTasks = (tasks?.filter(t => !t.mainTaskId && t.epicId === status.epicId) || [])
                            .filter(mainTask => tasks?.some(t => t.mainTaskId === mainTask.taskId));
                        return statusTasks.length > 0;
                    })
                    .map((status) => {
                    // Unfiltered set - drives the completed-group marker, which must
                    // stay true whether or not the hide toggle is on
                    const allStatusTasks = (tasks?.filter(t => !t.mainTaskId && t.epicId === status.epicId) || [])
                        .filter(mainTask => tasks?.some(t => t.mainTaskId === mainTask.taskId));
                    const groupComplete = allStatusTasks.length > 0 && allStatusTasks.every(t => t.isClosed === true);

                    // Visible set - rows, counts and progress all follow the toggle.
                    // NOTE: the .filter() above deliberately stays on the unfiltered
                    // list, so a fully hidden group still renders its header.
                    const statusTasks = hideCompletedEpics
                        ? allStatusTasks.filter(t => t.isClosed !== true)
                        : allStatusTasks;
                    const isExpanded = collapsedColumns.has(`status-${status.epicId}`);

                    // Calculate progress from all subtasks of tasks in this status
                    const allSubtasksForStatus = tasks?.filter(t =>
                        t.mainTaskId && statusTasks.some(st => st.taskId === t.mainTaskId)
                    ) || [];
                    const totalSubtasks = allSubtasksForStatus.length;
                    const closedSubtasks = allSubtasksForStatus.filter(t => t.isClosed).length;
                    const statusProgress = totalSubtasks > 0 ? Math.round((closedSubtasks / totalSubtasks) * 100) : 0;

                    return (
                        <React.Fragment key={status.epicId}>
                        {/* Status Row */}
                        <tr
                            className="hover:bg-purple-100 font-medium"
                            style={{
                                background: 'linear-gradient(to bottom, #faf7ff 0%, #f5f5f6 100%)',
                                boxShadow: 'inset 0 2px 0 0 #e9d5ff, inset 0 -1px 0 0 #e9d5ff'
                            }}
                        >
                        <td className="px-6 py-4">
                        <button
                        onClick={() => {
                            const newCollapsed = new Set(collapsedColumns);
                            if (isExpanded) {
                                newCollapsed.delete(`status-${status.epicId}`);
                            } else {
                                newCollapsed.add(`status-${status.epicId}`);
                            }
                            setCollapsedColumns(newCollapsed);
                        }}
                        className="text-gray-600 hover:text-gray-900"
                        >
                        {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                        </button>
                        </td>
                        <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                        {groupComplete && <FaCheck className="text-green-600 flex-shrink-0" size={12} />}
                        <span className={`font-medium ${groupComplete ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{status.name}</span>
                        <span className="text-xs text-gray-500">({statusTasks.length})</span>
                        </div>
                        </td>
                        <td className="px-6 py-4">
                        <span className="text-sm text-gray-400">-</span>
                        </td>
                        <td className="px-6 py-4">
                        <span className="text-sm text-gray-400">-</span>
                        </td>
                        <td className="px-6 py-4">
                        <span className="text-sm text-gray-400">-</span>
                        </td>
                        <td className="px-6 py-4">
                        <span className="text-sm text-gray-400">-</span>
                        </td>
                        {/* T473 - the epic's effort.
                            ⛔ COMPUTED FROM `coreMainTasks`, NOT from statusTasks
                            or allStatusTasks. statusTasks follows the
                            hide-completed toggle (D17 forbids that) and BOTH of
                            them drop main tasks with no subtasks, which still
                            count (D22). Add-in tasks are excluded (D23), and so
                            is the client's own swimlane (D1, ECL-01). */}
                        <td className="px-6 py-4">
                        {(() => {
                            const rows = coreMainTasks(tasks, {
                                epicId: status.epicId, teamSwimlaneId, project
                            });
                            const estimates = sumEffort(rows, 'effortEstimated');
                            const actuals = sumEffort(rows, 'effortActual');
                            const typed = status.effortEstimated;
                            return (
                                <div className="text-xs text-gray-600 whitespace-nowrap">
                                <span title="Estimate typed on this epic">Est {formatEffort(typed) ?? '-'}</span>
                                <span className="text-gray-300"> · </span>
                                <span title="Sum of this epic's main-task estimates">Planned {formatEffort(estimates.total)}</span>
                                <span className="text-gray-300"> · </span>
                                <span title="Sum of this epic's main-task actuals">Act {formatEffort(actuals.total)}</span>
                                <span className="text-gray-300"> · </span>
                                <span
                                    className={isOverrun(typed, actuals.total) ? 'text-red-600 font-semibold' : ''}
                                    title="Actual effort minus the estimate typed on this epic"
                                >
                                    {varianceLabel(typed, actuals.total)}
                                </span>
                                {estimates.unestimated > 0 && (
                                    <span
                                        className="text-amber-600"
                                        title="Main tasks in this epic with no estimate - the Planned total does not include them"
                                    >
                                        {' · '}{estimates.unestimated} unestimated
                                    </span>
                                )}
                                </div>
                            );
                        })()}
                        </td>
                        <td className="px-6 py-4">
                        {groupComplete ? (
                        <span className="text-xs text-gray-500">
                        {closedSubtasks}/{totalSubtasks} Sub Tasks Completed
                        </span>
                        ) : (
                        <div className="flex items-center gap-3">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                        className="h-2 rounded-full transition-all"
                        style={{ width: `${statusProgress}%`, background: getProgressGradient('#7c6b9e') }}
                        />
                        </div>
                        <span className="text-xs text-gray-600 min-w-[3rem] text-right">
                        {statusProgress}%
                        </span>
                        </div>
                        )}
                        </td>
                        </tr>

                        {/* Expanded Tasks within this status - sorted to match Gantt order */}
                        {isExpanded && [...statusTasks].sort((a, b) => {
                            const swimlaneDiff = (a.swimlaneOrder || 0) - (b.swimlaneOrder || 0);
                            if (swimlaneDiff !== 0) return swimlaneDiff;
                            return (a.order || 0) - (b.order || 0);
                        }).map((mainTask) => {
                            // Get ALL subtasks for progress calculation
                            const allSubtasks = tasks?.filter(t => t.mainTaskId === mainTask.taskId) || [];
                            const isMainTaskExpanded = collapsedColumns.has(`maintask-${mainTask.taskId}`);
                            const isTeamTask = mainTask.swimlaneId === teamSwimlaneId || (mainTask.swimlaneId === null && teamSwimlaneId === project?.default_swimlane);

                            return (
                                <React.Fragment key={mainTask.taskId}>
                                {/* Main Task Row */}
                                <tr
                                    className={`hover:bg-gray-100 ${hoveredMainTaskId === mainTask.taskId ? 'bg-yellow-50 shadow-[inset_4px_0_0_0_#facc15]' : ''}`}
                                    style={hoveredMainTaskId !== mainTask.taskId ? {
                                        background: isTeamTask ? 'linear-gradient(to bottom, #eff6ff 0%, #f3f4f6 100%)' : 'linear-gradient(to bottom, #f3fef6 0%, #f5f5f6 100%)',
                                        boxShadow: isTeamTask
                                            ? 'inset 3px 0 0 0 #3b82f6, inset 0 2px 0 0 #93c5fd'
                                            : 'inset 0 2px 0 0 #86efac'
                                    } : {}}
                                >
                                <td className="px-6 py-3">
                                {allSubtasks.length > 0 && (
                                    <button
                                    onClick={() => {
                                        const newCollapsed = new Set(collapsedColumns);
                                        if (isMainTaskExpanded) {
                                            newCollapsed.delete(`maintask-${mainTask.taskId}`);
                                        } else {
                                            newCollapsed.add(`maintask-${mainTask.taskId}`);
                                        }
                                        setCollapsedColumns(newCollapsed);
                                    }}
                                    className="text-gray-600 hover:text-gray-900 ml-6"
                                    >
                                    {isMainTaskExpanded ? <FaChevronDown className="text-xs" /> : <FaChevronRight className="text-xs" />}
                                    </button>
                                )}
                                </td>
                                <td className="px-6 py-3">
                                <div className="flex items-center gap-2 pl-4">
                                {mainTask.isClosed && <FaCheck className="text-green-600 flex-shrink-0" size={11} />}
                                <span
                                className={`text-sm hover:text-blue-600 cursor-pointer ${mainTask.isClosed ? 'text-gray-400 line-through' : 'text-gray-700'}`}
                                onMouseEnter={() => setHoveredMainTaskId(mainTask.taskId)}
                                onMouseLeave={() => setHoveredMainTaskId(null)}
                                onClick={() => fetchTaskDetail(mainTask.taskId)}
                                >
                                {mainTask.subject}
                                </span>
                                {allSubtasks.length > 0 && (
                                    <span className="text-xs text-gray-400">({allSubtasks.length})</span>
                                )}
                                </div>
                                </td>
                                <td className="px-6 py-3">
                                <span className="text-sm text-gray-600">
                                {project?.name || 'N/A'}
                                </span>
                                </td>
                                <td className="px-6 py-3">
                                {(() => {
                                    const { start, end } = getEffectiveDateRange(mainTask, tasks);
                                    if (!start && !end) return <span className="text-sm text-gray-400">-</span>;
                                    return (
                                        <div className="flex items-center gap-1 flex-wrap">
                                        {start && (
                                            <span className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded ${getStartDateBgColour(start)}`}>
                                            {formatDateUK(start)}
                                            </span>
                                        )}
                                        {start && end && <span className="text-gray-400 text-xs">-</span>}
                                        {end && (
                                            <span className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded ${getDueDateBgColour(end)}`}>
                                            {formatDateUK(end)}
                                            </span>
                                        )}
                                        </div>
                                    );
                                })()}
                                </td>
                                <td className="px-6 py-3" onClick={(e) => e.stopPropagation()}>
                                    <AssigneeDropdown
                                        task={mainTask}
                                        members={isInTMESwimlane(mainTask, data?.tasks || [], teamSwimlaneId) ? adminTeamMembers : (data?.members || [])}
                                        onAssign={handleQuickAssign}
                                        isSubtask={false}
                                    />
                                </td>
                                <td className="px-6 py-3">
                                {mainTask.milestone_name ? (
                                    <span className="text-sm text-gray-600">{mainTask.milestone_name}</span>
                                ) : (
                                    <span className="text-sm text-gray-400">-</span>
                                )}
                                </td>
                                {/* T473 - this main task's own effort.
                                    ⛔ NOT ON THE CLIENT'S LANE (D1, ECL-09), and
                                    NOT via the `isTeamTask` already in this scope:
                                    that is the unguarded inline form and answers
                                    true for EVERY task on a lane-less project, so
                                    the cell would empty while `coreMainTasks` -
                                    which has the guard - carried on counting the
                                    row. One predicate for every effort gate. */}
                                <td className="px-6 py-3">
                                {!isClientLaneTask(mainTask, teamSwimlaneId, project)
                                    && effortLabel(mainTask.effortEstimated, mainTask.effortActual) ? (
                                    <span
                                        className={`text-xs whitespace-nowrap ${
                                            isOverrun(mainTask.effortEstimated, mainTask.effortActual)
                                                ? 'text-red-600 font-semibold' : 'text-gray-600'
                                        }`}
                                        title="Estimated and actual effort, in days"
                                    >
                                        {effortLabel(mainTask.effortEstimated, mainTask.effortActual)}
                                    </span>
                                ) : (
                                    <span className="text-sm text-gray-400">-</span>
                                )}
                                </td>
                                <td className="px-6 py-3">
                                {(() => {
                                    const totalSubtasks = allSubtasks.length;
                                    const completedSubtasks = allSubtasks.filter(t => t.isClosed).length;

                                    // Was keyed off the epic being closed; closure now
                                    // lives on the task itself
                                    if (mainTask.isClosed) {
                                        return <span className="text-xs text-gray-500">Completed</span>;
                                    }
                                    if (totalSubtasks > 0) {
                                        const progress = Math.round((completedSubtasks / totalSubtasks) * 100);
                                        return (
                                            <div className="flex items-center gap-2">
                                            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                            <div
                                            className="h-1.5 rounded-full transition-all"
                                            style={{ width: `${progress}%`, background: getProgressGradient('#10b981') }}
                                            />
                                            </div>
                                            <span className="text-xs text-gray-500">
                                            {completedSubtasks}/{totalSubtasks}
                                            </span>
                                            </div>
                                        );
                                    }
                                    return <span className="text-sm text-gray-400">-</span>;
                                })()}
                                </td>
                                </tr>

                                {/* Expanded Subtasks - show all, closed ones have strikethrough, ordered by start date */}
                                {isMainTaskExpanded && (() => {
                                    const sortedSubtasks = [...allSubtasks].sort((a, b) => {
                                        if (!a.startDate && !b.startDate) return 0;
                                        if (!a.startDate) return 1;
                                        if (!b.startDate) return -1;
                                        return new Date(a.startDate) - new Date(b.startDate);
                                    });
                                    return sortedSubtasks.map((subtask, subtaskIdx) => {
                                    const closedStyle = subtask.isClosed ? 'text-gray-400 line-through' : '';
                                    const isLastSubtask = subtaskIdx === sortedSubtasks.length - 1;
                                    const rowBackground = subtask.isMeeting
                                        ? 'linear-gradient(to bottom, #faf6e8 0%, #f5eecc 100%)'
                                        : 'linear-gradient(to bottom, #eff0f3 0%, #f3f4f6 100%)';
                                    const blockers = blockedTasksMap?.get(subtask.taskId?.toString()) || [];
                                    const isBlocked = blockers.length > 0 && !subtask.isClosed;
                                    const hasIssueBlocker = blockers.some(b => b.type === 'issue');
                                    return (
                                    <tr
                                    key={subtask.taskId}
                                    className="hover:bg-gray-200 cursor-pointer"
                                    style={{
                                        background: rowBackground,
                                        ...(isBlocked ? { boxShadow: hasIssueBlocker ? 'inset 3px 0 0 0 #dc2626, inset 5px 0 0 0 #f3f4f6, inset 8px 0 0 0 #dc2626' : 'inset 4px 0 0 0 #dc2626' } : {})
                                    }}
                                    onClick={() => fetchTaskDetail(subtask.taskId)}
                                    >
                                    <td className="px-6 py-2"></td>
                                    <td className="px-6 py-2">
                                    <div className="flex items-center gap-2 pl-8">
                                    <span className={`text-sm hover:text-blue-600 ${subtask.isClosed ? 'text-gray-400 line-through' : 'text-gray-600'}`}>{subtask.subject}{subtask.isMeeting && <span className="text-yellow-600 ml-1">(Meeting)</span>}</span>
                                    </div>
                                    </td>
                                    <td className="px-6 py-2">
                                    <span className={`text-xs ${subtask.isClosed ? 'text-gray-400 line-through' : 'text-gray-500'}`}>{project?.name || 'N/A'}</span>
                                    </td>
                                    <td className="px-6 py-2">
                                    {subtask.startDate || subtask.dueDate ? (
                                        <div className={`flex items-center gap-1 flex-wrap ${closedStyle}`}>
                                        {subtask.startDate && (
                                            <span className={`inline-flex items-center px-1 py-0.5 text-xs rounded ${subtask.isClosed ? 'bg-gray-100 text-gray-400' : getStartDateBgColour(subtask.startDate)}`}>
                                            {formatDateUK(subtask.startDate)}
                                            </span>
                                        )}
                                        {subtask.startDate && subtask.dueDate && <span className="text-gray-400 text-xs">-</span>}
                                        {subtask.dueDate && (
                                            <span className={`inline-flex items-center px-1 py-0.5 text-xs rounded ${subtask.isClosed ? 'bg-gray-100 text-gray-400' : getDueDateBgColour(subtask.dueDate)}`}>
                                            {formatDateUK(subtask.dueDate)}
                                            </span>
                                        )}
                                        </div>
                                    ) : (
                                        <span className={`text-xs text-gray-400 ${closedStyle}`}>-</span>
                                    )}
                                    </td>
                                    <td className="px-6 py-2" onClick={(e) => e.stopPropagation()}>
                                        <AssigneeDropdown
                                            task={subtask}
                                            members={isInTMESwimlane(subtask, data?.tasks || [], teamSwimlaneId) ? adminTeamMembers : (data?.members || [])}
                                            onAssign={handleQuickAssign}
                                            isSubtask={true}
                                        />
                                    </td>
                                    <td
                                        className="px-6 py-2"
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseEnter={isBlocked ? (e) => handleBlockedMouseEnter(e, blockers) : undefined}
                                        onMouseLeave={isBlocked ? handleBlockedMouseLeave : undefined}
                                    >
                                        <StatusDropdown
                                            task={subtask}
                                            statuses={project?.subtaskStatuses}
                                            onStatusChange={handleQuickStatusChange}
                                            onClosureRequired={handleEpicsClosureRequired}
                                            isBlocked={isBlocked}
                                            blockers={blockers}
                                        />
                                    </td>
                                    {/* T473 - the Effort column. A SUBTASK never
                                        carries effort (D3), but the cell must exist
                                        or every column to its right shifts by one
                                        on subtask rows only. */}
                                    <td className="px-6 py-2"></td>
                                    <td className="px-6 py-2">
                                    {subtask.isClosed ? (
                                        <span className="text-xs text-gray-500">Completed</span>
                                    ) : (() => {
                                        const statusName = subtask.statusName || 'New';
                                        const progress = getSubtaskProgress(statusName, subtask.isClosed);
                                        return (
                                            <div className="flex items-center gap-3">
                                            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                            <div
                                            className="h-1.5 rounded-full"
                                            style={{
                                                width: `${progress.percent}%`,
                                                background: getProgressGradient(progress.colour),
                                                animation: progress.needsInfo ? 'needsInfoPulse 2s ease-in-out infinite' : 'none'
                                            }}
                                            />
                                            </div>
                                            <span className="text-xs text-gray-600 min-w-[3rem] text-right">
                                            {progress.percent}%
                                            </span>
                                            </div>
                                        );
                                    })()}
                                    </td>
                                    </tr>
                                    );
                                });
                                })()}
                                </React.Fragment>
                            );
                        })}

                        {/* No tasks message */}
                        {isExpanded && statusTasks.length === 0 && (
                            <tr className="bg-gray-50">
                            <td className="px-6 py-3"></td>
                            {/* colSpan follows the header: 8 columns, one taken
                                by the expander above, so this spans the other 7.
                                Was 6 before the Effort column (T473). */}
                            <td colSpan="7" className="px-6 py-3 text-sm text-gray-500 italic">
                            No tasks in this status
                            </td>
                            </tr>
                        )}
                        </React.Fragment>
                    );
                })}

                {/* Epic Grouping - for tasks assigned to Epics */}
                {(epics || []).filter(epic => {
                    const tasksWithThisEpic = (tasks || []).filter(t => !t.mainTaskId && t.epics && t.epics.some(e => e.id === epic.epicId));
                    return tasksWithThisEpic.some(t => tasks.some(st => st.mainTaskId === t.taskId));
                }).map((epic) => {
                    // Unfiltered set - drives the completed-group marker
                    const allEpicTasks = (tasks || [])
                        .filter(t => !t.mainTaskId && t.epics && t.epics.some(e => e.id === epic.epicId))
                        .filter(mainTask => tasks.some(t => t.mainTaskId === mainTask.taskId));
                    const groupComplete = allEpicTasks.length > 0 && allEpicTasks.every(t => t.isClosed === true);

                    // Visible set - rows, counts and progress follow the toggle.
                    // The .filter() above stays unfiltered so the header survives.
                    const epicTasks = hideCompletedEpics
                        ? allEpicTasks.filter(t => t.isClosed !== true)
                        : allEpicTasks;
                    const isExpanded = collapsedColumns.has(`epic-${epic.epicId}`);

                    // Calculate epic progress from all subtasks
                    const allEpicSubtasks = epicTasks.flatMap(t => tasks.filter(st => st.mainTaskId === t.taskId));
                    const epicProgress = allEpicSubtasks.length > 0
                        ? Math.round((allEpicSubtasks.filter(st => st.isClosed).length / allEpicSubtasks.length) * 100)
                        : 0;

                    return (
                        <React.Fragment key={`epic-${epic.epicId}`}>
                        {/* Epic Row - same styling as Status rows */}
                        <tr
                            className="hover:bg-purple-100 font-medium"
                            style={{
                                background: 'linear-gradient(to bottom, #faf7ff 0%, #f5f5f6 100%)',
                                boxShadow: 'inset 0 2px 0 0 #e9d5ff, inset 0 -1px 0 0 #e9d5ff'
                            }}
                        >
                            <td className="px-6 py-4">
                            <button
                            onClick={() => {
                                const newCollapsed = new Set(collapsedColumns);
                                if (isExpanded) {
                                    newCollapsed.delete(`epic-${epic.epicId}`);
                                } else {
                                    newCollapsed.add(`epic-${epic.epicId}`);
                                }
                                setCollapsedColumns(newCollapsed);
                            }}
                            className="text-gray-600 hover:text-gray-900"
                            >
                                {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                            </button>
                            </td>
                            <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                            {groupComplete && <FaCheck className="text-green-600 flex-shrink-0" size={12} />}
                            <span className={`font-medium ${groupComplete ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{epic.subject}</span>
                            <span className="text-xs text-gray-500">({epicTasks.length})</span>
                            </div>
                            </td>
                            <td className="px-6 py-4">
                            <span className="text-sm text-gray-400">-</span>
                            </td>
                            <td className="px-6 py-4">
                            <span className="text-sm text-gray-400">-</span>
                            </td>
                            <td className="px-6 py-4">
                            <span className="text-sm text-gray-400">-</span>
                            </td>
                            <td className="px-6 py-4">
                            <span className="text-sm text-gray-400">-</span>
                            </td>
                            {/* T473 - the Effort column, in the SECOND grouping
                                block. Same table, same header, so a cell missing
                                here shifts every column after it on these rows. */}
                            <td className="px-6 py-4">
                            <span className="text-sm text-gray-400">-</span>
                            </td>
                            <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                                <div className="flex-1 bg-gray-200 rounded-full h-2">
                                <div
                                    className="h-2 rounded-full transition-all"
                                    style={{ width: `${epicProgress}%`, background: getProgressGradient('#7c6b9e') }}
                                />
                                </div>
                                <span className="text-xs text-gray-600 min-w-[3rem] text-right">{epicProgress}%</span>
                            </div>
                            </td>
                        </tr>

                        {/* Expanded Tasks within Epic */}
                        {isExpanded && [...epicTasks].sort((a, b) => {
                            const swimlaneDiff = (a.swimlaneOrder || 0) - (b.swimlaneOrder || 0);
                            if (swimlaneDiff !== 0) return swimlaneDiff;
                            return (a.order || 0) - (b.order || 0);
                        }).map((mainTask) => {
                            const allSubtasks = tasks.filter(t => t.mainTaskId === mainTask.taskId);
                            const isMainTaskExpanded = collapsedColumns.has(`epic-maintask-${mainTask.taskId}`);
                            const isTeamTask = mainTask.swimlaneId === teamSwimlaneId || (mainTask.swimlaneId === null && teamSwimlaneId === data.project?.default_swimlane);

                            return (
                                <React.Fragment key={`epic-maintask-${mainTask.taskId}`}>
                                <tr
                                    className={`hover:bg-gray-100 ${hoveredMainTaskId === mainTask.taskId ? 'bg-yellow-50 shadow-[inset_4px_0_0_0_#facc15]' : ''}`}
                                    style={hoveredMainTaskId !== mainTask.taskId ? {
                                        background: isTeamTask ? 'linear-gradient(to bottom, #eff6ff 0%, #f3f4f6 100%)' : 'linear-gradient(to bottom, #f3fef6 0%, #f5f5f6 100%)',
                                        boxShadow: isTeamTask
                                            ? 'inset 3px 0 0 0 #3b82f6, inset 0 2px 0 0 #93c5fd'
                                            : 'inset 0 2px 0 0 #86efac'
                                    } : {}}
                                >
                                <td className="px-6 py-2">
                                {allSubtasks.length > 0 && (
                                    <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const newCollapsed = new Set(collapsedColumns);
                                        if (isMainTaskExpanded) {
                                            newCollapsed.delete(`epic-maintask-${mainTask.taskId}`);
                                        } else {
                                            newCollapsed.add(`epic-maintask-${mainTask.taskId}`);
                                        }
                                        setCollapsedColumns(newCollapsed);
                                    }}
                                    className="text-gray-600 hover:text-gray-900 ml-2"
                                    >
                                    {isMainTaskExpanded ? <FaChevronDown className="text-xs" /> : <FaChevronRight className="text-xs" />}
                                    </button>
                                )}
                                </td>
                                <td className="px-6 py-2">
                                <div className="flex items-center gap-2 pl-4">
                                {mainTask.isClosed && <FaCheck className="text-green-600 flex-shrink-0" size={11} />}
                                <span
                                className={`text-sm hover:text-blue-600 cursor-pointer ${mainTask.isClosed ? 'text-gray-400 line-through' : 'text-gray-700'}`}
                                onMouseEnter={() => setHoveredMainTaskId(mainTask.taskId)}
                                onMouseLeave={() => setHoveredMainTaskId(null)}
                                onClick={() => fetchTaskDetail(mainTask.taskId)}
                                >
                                {mainTask.subject}
                                </span>
                                {allSubtasks.length > 0 && (
                                    <span className="text-xs text-gray-400">({allSubtasks.length})</span>
                                )}
                                </div>
                                </td>
                                <td className="px-6 py-2">
                                <span className="text-sm text-gray-600">{data.project?.name || 'N/A'}</span>
                                </td>
                                <td className="px-6 py-2">
                                {(() => {
                                    const { start, end } = getEffectiveDateRange(mainTask, tasks);
                                    if (!start && !end) return <span className="text-sm text-gray-400">-</span>;
                                    return (
                                        <div className="flex items-center gap-1 flex-wrap">
                                        {start && (
                                            <span className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded ${getStartDateBgColour(start)}`}>
                                            {formatDateUK(start)}
                                            </span>
                                        )}
                                        {start && end && <span className="text-gray-400 text-xs">-</span>}
                                        {end && (
                                            <span className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded ${getDueDateBgColour(end)}`}>
                                            {formatDateUK(end)}
                                            </span>
                                        )}
                                        </div>
                                    );
                                })()}
                                </td>
                                <td className="px-6 py-2" onClick={(e) => e.stopPropagation()}>
                                    <AssigneeDropdown
                                        task={mainTask}
                                        members={isInTMESwimlane(mainTask, data?.tasks || [], teamSwimlaneId) ? adminTeamMembers : (data?.members || [])}
                                        onAssign={handleQuickAssign}
                                        isSubtask={false}
                                    />
                                </td>
                                <td className="px-6 py-2">
                                {mainTask.milestone_name ? (
                                    <span className="text-sm text-gray-600">{mainTask.milestone_name}</span>
                                ) : (
                                    <span className="text-sm text-gray-400">-</span>
                                )}
                                </td>
                                {/* T473 - effort, in the second grouping block.
                                    Same lane gate as the first (D1, ECL-09) - a
                                    row appears under BOTH groupings, so a gate on
                                    one is invisible in the other. */}
                                <td className="px-6 py-2">
                                {!isClientLaneTask(mainTask, teamSwimlaneId, project)
                                    && effortLabel(mainTask.effortEstimated, mainTask.effortActual) ? (
                                    <span
                                        className={`text-xs whitespace-nowrap ${
                                            isOverrun(mainTask.effortEstimated, mainTask.effortActual)
                                                ? 'text-red-600 font-semibold' : 'text-gray-600'
                                        }`}
                                        title="Estimated and actual effort, in days"
                                    >
                                        {effortLabel(mainTask.effortEstimated, mainTask.effortActual)}
                                    </span>
                                ) : (
                                    <span className="text-sm text-gray-400">-</span>
                                )}
                                </td>
                                <td className="px-6 py-2">
                                {(() => {
                                    const totalSubtasks = allSubtasks.length;
                                    const completedSubtasks = allSubtasks.filter(t => t.isClosed).length;
                                    if (totalSubtasks > 0) {
                                        const progress = Math.round((completedSubtasks / totalSubtasks) * 100);
                                        return (
                                            <div className="flex items-center gap-2">
                                            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                            <div
                                            className="h-1.5 rounded-full transition-all"
                                            style={{ width: `${progress}%`, background: getProgressGradient('#10b981') }}
                                            />
                                            </div>
                                            <span className="text-xs text-gray-500">{completedSubtasks}/{totalSubtasks}</span>
                                            </div>
                                        );
                                    }
                                    return <span className="text-sm text-gray-400">-</span>;
                                })()}
                                </td>
                                </tr>

                                {/* Subtasks within Epic task */}
                                {isMainTaskExpanded && [...allSubtasks].sort((a, b) => {
                                    if (!a.startDate && !b.startDate) return 0;
                                    if (!a.startDate) return 1;
                                    if (!b.startDate) return -1;
                                    return new Date(a.startDate) - new Date(b.startDate);
                                }).map((subtask) => {
                                    const closedStyle = subtask.isClosed ? 'text-gray-400 line-through' : '';
                                    const rowBackground = subtask.isMeeting
                                        ? 'linear-gradient(to bottom, #faf6e8 0%, #f5eecc 100%)'
                                        : 'linear-gradient(to bottom, #eff0f3 0%, #f3f4f6 100%)';
                                    const blockers = blockedTasksMap?.get(subtask.taskId?.toString()) || [];
                                    const isBlocked = blockers.length > 0 && !subtask.isClosed;
                                    const hasIssueBlocker = blockers.some(b => b.type === 'issue');
                                    return (
                                    <tr
                                    key={`epic-subtask-${subtask.taskId}`}
                                    className="hover:bg-gray-200 cursor-pointer"
                                    style={{
                                        background: rowBackground,
                                        ...(isBlocked ? { boxShadow: hasIssueBlocker ? 'inset 3px 0 0 0 #dc2626, inset 5px 0 0 0 #f3f4f6, inset 8px 0 0 0 #dc2626' : 'inset 4px 0 0 0 #dc2626' } : {})
                                    }}
                                    onClick={() => fetchTaskDetail(subtask.taskId)}
                                    >
                                    <td className="px-6 py-2"></td>
                                    <td className="px-6 py-2">
                                    <div className="flex items-center gap-2 pl-8">
                                    <span className={`text-sm ${subtask.isClosed ? 'text-gray-400 line-through' : 'text-gray-600'}`}>{subtask.subject}{subtask.isMeeting && <span className="text-yellow-600 ml-1">(Meeting)</span>}</span>
                                    </div>
                                    </td>
                                    <td className="px-6 py-2">
                                    <span className={`text-xs ${subtask.isClosed ? 'text-gray-400 line-through' : 'text-gray-500'}`}>{data.project?.name || 'N/A'}</span>
                                    </td>
                                    <td className="px-6 py-2">
                                    {subtask.startDate || subtask.dueDate ? (
                                        <div className={`flex items-center gap-1 flex-wrap ${closedStyle}`}>
                                        {subtask.startDate && (
                                            <span className={`inline-flex items-center px-1 py-0.5 text-xs rounded ${subtask.isClosed ? 'bg-gray-100 text-gray-400' : getStartDateBgColour(subtask.startDate)}`}>
                                            {formatDateUK(subtask.startDate)}
                                            </span>
                                        )}
                                        {subtask.startDate && subtask.dueDate && <span className="text-gray-400 text-xs">-</span>}
                                        {subtask.dueDate && (
                                            <span className={`inline-flex items-center px-1 py-0.5 text-xs rounded ${subtask.isClosed ? 'bg-gray-100 text-gray-400' : getDueDateBgColour(subtask.dueDate)}`}>
                                            {formatDateUK(subtask.dueDate)}
                                            </span>
                                        )}
                                        </div>
                                    ) : (
                                        <span className={`text-xs text-gray-400 ${closedStyle}`}>-</span>
                                    )}
                                    </td>
                                    <td className="px-6 py-2" onClick={(e) => e.stopPropagation()}>
                                        <AssigneeDropdown
                                            task={subtask}
                                            members={isInTMESwimlane(subtask, data?.tasks || [], teamSwimlaneId) ? adminTeamMembers : (data?.members || [])}
                                            onAssign={handleQuickAssign}
                                            isSubtask={true}
                                        />
                                    </td>
                                    <td
                                        className="px-6 py-2"
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseEnter={isBlocked ? (e) => handleBlockedMouseEnter(e, blockers) : undefined}
                                        onMouseLeave={isBlocked ? handleBlockedMouseLeave : undefined}
                                    >
                                        <StatusDropdown
                                            task={subtask}
                                            statuses={project?.subtaskStatuses}
                                            onStatusChange={handleQuickStatusChange}
                                            onClosureRequired={handleEpicsClosureRequired}
                                            isBlocked={isBlocked}
                                            blockers={blockers}
                                        />
                                    </td>
                                    {/* T473 - the Effort column. Subtasks carry no
                                        effort, but the cell must exist or the
                                        columns shift on these rows. */}
                                    <td className="px-6 py-2"></td>
                                    <td className="px-6 py-2">
                                    {(() => {
                                        const statusName = subtask.statusName || 'New';
                                        const progress = getSubtaskProgress(statusName, subtask.isClosed);
                                        return (
                                            <div className="flex items-center gap-2">
                                            <div className={`flex-1 bg-gray-200 rounded-full h-1.5 ${progress.needsInfo && !subtask.isClosed ? 'animate-pulse' : ''}`}>
                                            <div
                                            className="h-1.5 rounded-full transition-all"
                                            style={{ width: `${progress.percent}%`, background: getProgressGradient(progress.colour) }}
                                            />
                                            </div>
                                            <span className="text-xs text-gray-500">{progress.percent}%</span>
                                            </div>
                                        );
                                    })()}
                                    </td>
                                    </tr>
                                    );
                                })}
                                </React.Fragment>
                            );
                        })}
                        </React.Fragment>
                    );
                })}
                </tbody>
                </table>
                </div>
            )}
            </div>
            </div>
        );
        })()}

        {/* Board Area */}
        <div className="flex-1 overflow-hidden min-h-0">
        <div className="w-full h-full mx-auto p-2">
        {currentView === 'kanban' && (
            project && columns.length > 0 ? (
                <KanbanBoard
                project={project}
                tasks={tasks || []}
                columns={columns}
                members={members || []}
                adminTeamMembers={adminTeamMembers}
                collapsedColumns={collapsedColumns}
                setCollapsedColumns={setCollapsedColumns}
                collapsedSwimlanes={collapsedSwimlanes}
                setCollapsedSwimlanes={setCollapsedSwimlanes}
                hoveredMainTaskId={hoveredMainTaskId}
                setHoveredMainTaskId={setHoveredMainTaskId}
                teamSwimlaneId={teamSwimlaneId}
                draggedTask={draggedTask}
                onDragStart={(task) => { draggedTaskRef.current = task; setDraggedTask(task); }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(statusId) => {
                    handleDropWithClosureCheck(draggedTaskRef.current, statusId, columns, updateInProgressRef);
                    draggedTaskRef.current = null;
                    setDraggedTask(null);
                }}
                onTaskClick={fetchTaskDetail}
                onCreateTaskClick={(swimlaneId) => { setCreateTaskSwimlaneId(swimlaneId); setShowCreateTaskModal(true); }}
                isAdmin={true}
                isAdminPortal={true}
                lead={lead}
                readOnly={false}
                edgeColumnsInitialised={edgeColumnsInitialisedRef.current}
                blockedTasksMap={blockedTasksMap}
                onEpicChanged={fetchClientDashboard}
                />
            ) : (
                <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                No project data available
                </div>
            )
        )}

        {/* h-full, NOT flex-1: this div's parent is the plain-block board
            container, so flex-1 is inert and the div collapsed to auto height
            - the chart then sized to its full row count and the footer date
            slider fell below the overflow-hidden fold (BUG-029). h-full works
            because the board container's own height is definite. */}
        <div className={ganttFullScreen && currentView === 'gantt' ? 'h-full min-h-0 overflow-hidden p-2' : 'contents'}>
        {currentView === 'gantt' && (
            loading ? (
                <div className="h-full flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    <p className="mt-4 text-gray-600">Loading Gantt...</p>
                </div>
            ) : data ? (
                <>
                {data.invalidDeps?.length > 0 && (
                    <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-2 mb-2 rounded text-sm font-medium">
                        {data.invalidDeps.length} non-canonical dependency reference{data.invalidDeps.length === 1 ? '' : 's'} not shown - run scripts/migrate-dependson-canonical.sh
                    </div>
                )}
                {/* Interrupted project shift (plan §9.1, D13/D14): Resume ONLY -
                    a new shift is blocked while the entry exists, and Dismiss
                    hides the banner without clearing the recovery key. */}
                {pendingShift && !pendingShiftDismissed && (
                    <div className="bg-amber-50 border border-amber-300 text-amber-900 px-4 py-2 mb-2 rounded text-sm font-medium flex items-center justify-between">
                        <span>
                            A shift of {Math.abs(pendingShift.deltaDays / 7)} week{Math.abs(pendingShift.deltaDays / 7) !== 1 ? 's' : ''} was interrupted. Resume?
                        </span>
                        <span className="flex gap-2">
                            <button
                                onClick={handleResumeShift}
                                className="bg-amber-600 hover:bg-amber-700 text-white px-3 py-1 rounded"
                            >
                                Resume shift
                            </button>
                            <button
                                onClick={() => setPendingShiftDismissed(true)}
                                className="border border-amber-400 hover:bg-amber-100 px-3 py-1 rounded"
                            >
                                Dismiss
                            </button>
                        </span>
                    </div>
                )}
                <GanttChart
                    tasks={(data.tasks || [])
                        .filter(task => !task.mainTaskId)
                        .map(task => ({
                            taskId: task.taskId,
                            text: task.subject,
                            subject: task.subject,
                            start: task.startDate || task.dueDate,
                            end: task.dueDate,
                            dueDate: task.dueDate,
                            progress: task.progress || 0,
                            status: task.statusName || task.epicId,
                            statusId: task.epicId,
                            statusName: task.statusName,
                            isClosed: task.isClosed,
                            swimlaneId: task.swimlaneId,
                            swimlaneOrder: task.swimlaneOrder || 0,
                            order: task.order || 0,
                            epics: task.epics,
                            // T475/T471 - this prop is an EXPLICIT MAPPING, so
                            // effort has to be listed here or the row builder
                            // never sees it and both the column and the bar
                            // label come out empty.
                            effortEstimated: task.effortEstimated,
                            effortActual: task.effortActual
                        }))}
                    showEffort={true}
                    statuses={columns.map(c => ({ ...c, id: c.epicId }))}
                    epics={data.epics || []}
                    teamSwimlaneId={teamSwimlaneId}
                    subtasks={optimisticState.subtasks.map(st => ({
                        taskId: st.taskId,
                        text: st.subject,
                        subject: st.subject,
                        start: st.startDate || st.dueDate,
                        end: st.dueDate,
                        dueDate: st.dueDate,
                        progress: st.isClosed ? 100 : 0,
                        status: st.statusName || st.status,
                        statusName: st.statusName,
                        isClosed: st.isClosed,
                        mainTaskId: st.mainTaskId,
                        parentId: st.mainTaskId,
                        assignedToName: st.assignedToName,
                        isMeeting: st.isMeeting || false,
                        meetingType: st.meetingType || null,
                        agenda: st.agenda || null,
                        links: st.links || [],
                        order: st.order || 0
                    }))}
                    links={optimisticState.links}
                    onTaskClick={fetchTaskDetail}
                    readOnly={false}
                    onDependencyCreate={handleDependencyCreate}
                    onDependencyDelete={handleDependencyDelete}
                    onDependencyColorChange={handleDependencyColorChange}
                    onTaskUpdate={handleTaskUpdate}
                    onParentTaskMove={handleParentTaskMove}
                    onSubtaskOrderChange={saveSubtaskOrder}
                    onMainTaskOrderChange={saveMainTaskOrder}
                    taskStatuses={data.project?.subtaskStatuses || []}
                    onStatusChange={handleStatusChange}
                    weekendWork={optimisticState.weekendWork}
                    onWeekendWorkChange={saveWeekendWork}
                    kickOffDate={optimisticState.kickOffDate}
                    onKickOffDateChange={saveKickOffDate}
                    onProjectDateShift={handleProjectDateShift}
                    shiftProgress={shiftProgress}
                    blockedTasksMap={blockedTasksMap}
                    members={members}
                    onAssigneeChange={async (taskId, userId) => {
                        await handleQuickAssign(taskId, userId, true);
                    }}
                    onClosureRequired={handleGanttClosureRequired}
                    fullScreen={ganttFullScreen}
                    onToggleFullScreen={setGanttFullScreen}
                />
                </>
            ) : (
                <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                No project data available
                </div>
            )
        )}
        </div>
        {/* NOTE the board container stays OPEN here. f959e6b closed it at this
            point by mistake: the Risks and Resource blocks fell out of the
            w-full/h-full board div and landed AFTER it - an empty board still
            stretches to 100% height, so inside the overflow-hidden area both
            views rendered below the fold, invisible (2026-08-21 incident). */}

        {currentView === 'risks' && (
            (loadingRisks && loadingIssues) ? (
                <div className="h-full flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    <p className="mt-4 text-gray-600">Loading...</p>
                </div>
            ) : (
                <div className="h-full flex flex-col gap-2">
                    {/* Risks Section */}
                    <div className={`bg-white rounded-lg shadow flex flex-col overflow-hidden ${!risksIssuesCollapsed.has('risks') ? 'flex-1 min-h-0' : ''}`}>
                        {/* Collapsible Header */}
                        <div
                            className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => toggleRisksIssuesSection('risks')}
                        >
                            <div className="flex items-center gap-2">
                                {risksIssuesCollapsed.has('risks') ? (
                                    <FaChevronRight className="text-gray-500" />
                                ) : (
                                    <FaChevronDown className="text-gray-500" />
                                )}
                                <h2 className="text-lg font-semibold text-gray-700">Risk Register</h2>
                                <span className="text-sm text-gray-500">({risks.length})</span>
                            </div>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowRiskCreate(true);
                                }}
                                className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 text-white rounded hover:bg-primary-700 text-sm"
                            >
                                <FaPlus size={12} />
                                Add Risk
                            </button>
                        </div>

                        {/* Content */}
                        {!risksIssuesCollapsed.has('risks') && (
                            <div className="flex-1 overflow-hidden">
                                <RisksTable
                                    risks={risks}
                                    members={data?.members || []}
                                    onRiskClick={setSelectedRisk}
                                    onRefresh={fetchRisks}
                                    leadId={leadId}
                                    isAdmin={true}
                                    hideHeader={true}
                                    showCreate={showRiskCreate}
                                    onShowCreateChange={setShowRiskCreate}
                                />
                            </div>
                        )}
                    </div>

                    {/* Issues Section */}
                    <div className={`bg-white rounded-lg shadow flex flex-col overflow-hidden ${!risksIssuesCollapsed.has('issues') ? 'flex-1 min-h-0' : ''}`}>
                        {/* Collapsible Header */}
                        <div
                            className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => toggleRisksIssuesSection('issues')}
                        >
                            <div className="flex items-center gap-2">
                                {risksIssuesCollapsed.has('issues') ? (
                                    <FaChevronRight className="text-gray-500" />
                                ) : (
                                    <FaChevronDown className="text-gray-500" />
                                )}
                                <h2 className="text-lg font-semibold text-gray-700">Issues</h2>
                                <span className="text-sm text-gray-500">({issues.length})</span>
                                {issues.filter(i => i.status === 'Resolved by Client').length > 0 && (
                                    <span className="px-2 py-0.5 rounded-full bg-teal-100 text-teal-800 text-xs font-semibold">
                                        {issues.filter(i => i.status === 'Resolved by Client').length} awaiting approval
                                    </span>
                                )}
                            </div>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowIssueCreate(true);
                                }}
                                className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 text-white rounded hover:bg-primary-700 text-sm"
                            >
                                <FaPlus size={12} />
                                Add Issue
                            </button>
                        </div>

                        {/* Content */}
                        {!risksIssuesCollapsed.has('issues') && (
                            <div className="flex-1 overflow-hidden">
                                <IssuesTable
                                    issues={issues}
                                    members={issueAssignees}
                                    tasks={data?.tasks || []}
                                    onRefresh={fetchIssues}
                                    leadId={leadId}
                                    isAdmin={true}
                                    hideHeader={true}
                                    showCreate={showIssueCreate}
                                    onShowCreateChange={setShowIssueCreate}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )
        )}

        {currentView === 'resource' && (
            loading ? (
                <div className="h-full flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    <p className="mt-4 text-gray-600">Loading Resource...</p>
                </div>
            ) : data ? (
                <ResourceView
                    tasks={(data.tasks || []).filter(task => !task.mainTaskId && !task.isClosed)}
                    members={data.members || []}
                    onTaskClick={fetchTaskDetail}
                />
            ) : (
                <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                No resource data available
                </div>
            )
        )}
        </div>
        </div>
        </div>

        {/* Task Detail Modal - Admin Editable */}
        <TaskDetailsModal
        task={selectedTaskDetail}
        isOpen={!!selectedTaskDetail}
        onClose={() => setSelectedTaskDetail(null)}
        onUpdate={(taskId, updates) => {
            const { updates: clamped, adjustments, impossible, endBeforeStart } =
                clampTaskDateUpdates(optimisticState.subtasks, optimisticState.links, taskId, updates);
            if (endBeforeStart) { alertEndBeforeStart(endBeforeStart); return; }
            if (impossible) { alertImpossibleRange(impossible); return; }
            if (!confirmClampAdjustments(adjustments)) return;
            const { proceed, override } = confirmProjectWindow({
                start: clamped.start,
                dueDate: clamped.due_date || clamped.dueDate,
                kickoffDate: optimisticState.kickOffDate,
                label: 'This task'
            });
            if (!proceed) return;
            return handleSaveChanges(taskId, override ? { ...clamped, allowBeforeKickoff: true } : clamped);
        }}
        onDelete={handleDeleteTask}
        onCommentAdd={handleAddComment}
        onCommentEdit={handleEditComment}
        onCommentDelete={handleDeleteComment}
        onSubtaskCreate={(parentId, subtaskData) => {
            const { proceed, override } = confirmProjectWindow({
                start: subtaskData.start,
                dueDate: subtaskData.dueDate,
                kickoffDate: optimisticState.kickOffDate,
                label: subtaskData.subject || 'This subtask'
            });
            if (!proceed) return;
            return handleCreateSubtask(parentId, override ? { ...subtaskData, allowBeforeKickoff: true } : subtaskData, teamSwimlaneId);
        }}
        onSubtaskClick={fetchTaskDetail}
        isLoading={loadingTaskDetail}
        projectName={project?.name}
        columns={columns}
        taskStatuses={project?.subtaskStatuses || []}
        teamSwimlaneId={teamSwimlaneId}
        members={members || []}
        tasks={tasks || []}
        lead={lead}
        savingChanges={savingChanges}
        savingField={savingField}
        addingComment={addingComment}
        editingComment={editingComment}
        deletingComment={deletingComment}
        creatingSubtask={creatingSubtask}
        projectMembers={members || []}
        onOpenAssignmentEditor={() => {}}
        readOnly={false}
        isAdmin={true}
        blockedTasksMap={blockedTasksMap}
        adminTeamMembers={adminTeamMembers}
        showScripts={true}
        showWikiLinks={true}
        />

        {/* Loading Modal - only show when no task modal is already open */}
        {loadingTaskDetail && !selectedTaskDetail && (
            <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8">
            <div className="text-center">
            <div className="text-xl text-gray-600">Loading task details...</div>
            </div>
            </div>
            </div>
        )}

        {/* Project Links Modal */}
        <ProjectLinksModal
            isOpen={showLinksModal}
            onClose={() => setShowLinksModal(false)}
            apiUrl={process.env.REACT_APP_API_URL}
            apiFetch={apiFetch}
            fetchUrl={`${process.env.REACT_APP_API_URL}/admin/project-links/${leadId}`}
        />

        {/* All Tasks Modal.
            T476/D4: `showEffort` is passed HERE because this is the admin
            portal. ClientPortalLayout renders the same modal and passes
            nothing, and the prop defaults to false. */}
        <AllTasksModal
            isOpen={showAllTasksModal}
            onClose={() => setShowAllTasksModal(false)}
            apiUrl={process.env.REACT_APP_API_URL}
            apiFetch={apiFetch}
            fetchUrl={`${process.env.REACT_APP_API_URL}/admin/client-dashboard/${leadId}`}
            members={members}
            adminMembers={adminTeamMembers}
            onQuickAssign={handleQuickAssign}
            blockedTasksMap={blockedTasksMap}
            showEffort={true}
        />

        {/* Create Task Modal */}
        <CreateTaskModal
            isOpen={showCreateTaskModal}
            onClose={() => setShowCreateTaskModal(false)}
            onCreate={(taskData) => {
                handleCreateTask(taskData, createTaskSwimlaneId || teamSwimlaneId);
                setShowCreateTaskModal(false);
            }}
            projectName={project?.name}
            isCreating={creatingTask}
            members={createTaskSwimlaneId !== teamSwimlaneId ? adminTeamMembers : (members || [])}
            lead={lead}
            isAdmin={true}
        />

        {/* Blocked tooltip */}
        {blockedTooltip && (
            <div
                className="fixed z-50 px-3 py-2 text-sm bg-gray-800 text-white rounded-lg max-w-xs"
                style={{
                    left: blockedTooltip.x,
                    top: blockedTooltip.y,
                    transform: 'translate(-50%, -100%)',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2)'
                }}
                onMouseEnter={() => {
                    if (blockedTooltipTimeoutRef.current) clearTimeout(blockedTooltipTimeoutRef.current);
                }}
                onMouseLeave={() => setBlockedTooltip(null)}
            >
                <div className="font-bold text-red-400">Blocked by:</div>
                {blockedTooltip.content.map((b, i) => (
                    <div key={i} className="pl-2 text-red-300">
                        {b.type === 'issue'
                            ? `Issue ${b.id}: ${b.title}`
                            : b.parentTitle
                                ? `${b.parentTitle} / ${b.title}`
                                : b.title
                        }
                    </div>
                ))}
                <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800" />
            </div>
        )}

        {/* Closure Comment Modal */}
        <ClosureCommentModal
            isOpen={closureModal.isOpen}
            taskTitle={closureModal.taskTitle}
            isMeeting={closureModal.isMeeting}
            onConfirm={confirmClosure}
            onCancel={cancelClosure}
            isSubmitting={isSubmittingClosure}
        />

        {/* Attach Report Modal */}
        <AttachReportModal
            isOpen={showAttachReport}
            onClose={handleCloseAttachReport}
            report={attachReport}
        />

        <ShiftSummaryModal
            isOpen={!!shiftSummary}
            onClose={() => setShiftSummary(null)}
            summary={shiftSummary}
        />
        </div>
    );
};

export default AdminClientView;
